<template>
  <div class="create-page" :class="{ 'flow-page': currentStep !== 'create' }">
    <section v-if="currentStep === 'create'" class="create-panel">
      <div class="title-icon">
        <img :src="aiCreateImage" alt="" class="title-icon-img" />
      </div>
      <h1 class="create-title" aria-label="AI一键创作绘本">
        <svg class="create-title__svg" viewBox="0 0 270 36" aria-hidden="true" focusable="false">
          <defs>
            <linearGradient id="create-title-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#6033e1" />
              <stop offset="22%" stop-color="#6147ff" />
              <stop offset="46%" stop-color="#d451ff" />
              <stop offset="75%" stop-color="#ec458d" />
              <stop offset="100%" stop-color="#ffca8b" />
            </linearGradient>
          </defs>
          <text
            x="135"
            y="30"
            fill="url(#create-title-gradient)"
            font-size="30"
            font-weight="500"
            text-anchor="middle"
          >
            AI一键创作绘本
          </text>
        </svg>
      </h1>

      <div class="prompt-card">
        <PromptMentionEditor
          ref="promptEditorRef"
          v-model="form.description"
          :characters="mentionCharacters"
          :placeholder="promptPlaceholder"
          :maxlength="800"
          @mention-trigger="handleMentionTrigger"
        />
        <div class="prompt-footer">
          <div class="prompt-footer-leading">
            <div ref="mentionWrapRef" class="mention-wrap">
              <button
                class="mention-btn"
                type="button"
                @mousedown.prevent="savePromptCaret"
                @touchstart="savePromptCaret"
                @click.stop="toggleMentionPopup"
              >
                @
              </button>
              <button
                v-if="!mentionModalVisible"
                class="mention-tip"
                :class="{ 'mention-tip--persistent': !hasOpenedMentionPopup }"
                type="button"
                @mousedown.prevent="savePromptCaret"
                @touchstart="savePromptCaret"
                @click.stop="toggleMentionPopup"
              >
                选择角色
              </button>
            </div>
          </div>
          <span class="count-text">{{ form.description.length }}/800</span>
        </div>
      </div>

      <Teleport to="body" :disabled="!isMobileViewportActive">
        <div
          v-if="mentionModalVisible"
          class="mention-popup-root"
          :class="{ 'mention-popup-root--mobile': isMobileViewportActive }"
        >
          <div
            v-if="isMobileViewportActive"
            class="mention-mobile-overlay"
            @click.self="closeMentionPopup"
          ></div>
          <div
            class="mention-popup"
            :class="{ 'mention-popup--mobile': isMobileViewportActive }"
            :style="mentionPopupStyle"
            @click.stop
          >
            <header v-if="isMobileViewportActive" class="mention-popup-header">
              <span class="mention-popup-title">选择角色</span>
              <button
                class="mention-popup-close"
                type="button"
                aria-label="关闭"
                @click="closeMentionPopup"
              >
                ×
              </button>
            </header>
            <div v-if="mentionLoading" class="mention-loading">
              <span class="loading-spinner"></span>
              <span>加载角色中...</span>
            </div>
            <div v-else-if="!mentionCharacters.length" class="mention-empty">
              暂无角色，请先创建
            </div>
            <div v-else class="mention-grid">
              <button
                v-for="character in mentionCharacters"
                :key="character.id"
                class="mention-char-card"
                :class="{ active: selectedMentionId === character.id }"
                type="button"
                @click="selectMentionCharacter(character)"
              >
                <img
                  v-if="character.avatarUrl"
                  :src="character.avatarUrl"
                  :alt="character.name"
                  class="mention-char-img"
                />
                <div v-else class="mention-char-placeholder">
                  {{ character.name?.[0] ?? '?' }}
                </div>
                <strong class="mention-char-name">{{ character.name }}</strong>
                <span class="mention-char-setting">{{ character.setting || '暂无设定' }}</span>
              </button>
            </div>
            <button class="mention-create-btn" type="button" @click="goCreateCharacter">
              <span class="btn-spark">✦</span>
              <span>创建角色</span>
            </button>
          </div>
        </div>
      </Teleport>

      <div class="suggest-section">
        <div class="suggest-title">
          <span class="suggest-title-line" aria-hidden="true"></span>
          <span class="suggest-title-text">没有灵感? 试试这些</span>
          <span class="suggest-title-line" aria-hidden="true"></span>
        </div>
        <div class="suggest-tags">
          <button
            v-for="(tag, index) in promptTags"
            :key="`${tag}-${index}`"
            type="button"
            class="suggest-tag"
            @click="usePrompt(tag)"
          >
            {{ tag }}
          </button>
        </div>
      </div>

      <div class="options-grid">
        <div
          v-for="item in optionFields"
          :key="item.key"
          class="option-field"
          :class="`option-field--${item.key}`"
        >
          <span class="option-label">{{ item.label }}</span>
          <button
            v-if="item.key === 'artStyle'"
            class="option-select option-trigger option-art-style-trigger"
            type="button"
            @click="openArtStyleModal"
          >
            <img
              class="option-art-preview"
              :src="currentArtStyleOption.url"
              :alt="`${currentArtStyleOption.label}风格预览`"
            />
            <span>{{ currentArtStyleOption.label }}</span>
            <SvgIcon class="option-chevron" name="create-option-chevron" :size="12" />
          </button>
          <CreateOptionSelect
            v-else
            v-model="form[item.key]"
            :options="item.options"
            :sheet-title="item.sheetTitle"
          />
        </div>
      </div>

      <div v-if="isCreatePageActive" class="generate-btn-wrap">
        <button
          class="generate-btn"
          type="button"
          :disabled="generating || isCheckingStorage || !form.description.trim()"
          @click="handleGenerate"
        >
          <span>{{ generating || isCheckingStorage ? '创作中...' : '立即创作' }}</span>
          <img class="generate-btn-spark" :src="createGenerateSparkIcon" alt="" />
        </button>
      </div>
    </section>

    <section
      v-else-if="currentStep === 'script'"
      class="flow-panel script-panel"
      :class="{ 'script-panel--loading': isOutlineGenerating }"
    >
      <div class="flow-content">
        <div class="flow-head">
          <div class="flow-title-wrap">
            <h2 class="flow-title">故事内容</h2>
            <span v-if="displayScriptPageCount != null">{{ displayScriptPageCount }}页</span>
          </div>
          <button
            class="refresh-btn"
            type="button"
            :disabled="generating || generatingStoryboard"
            @click="refreshScript"
          >
            <span>↻</span>
            <span>换一换</span>
          </button>
        </div>

        <div
          ref="scriptListRef"
          class="script-list"
          :class="{ 'script-list--loading': isOutlineGenerating }"
        >
          <div v-if="isOutlineGenerating" class="script-loading">
            <template v-if="isOutlineQueued">
              <span class="outline-queue-spinner" aria-hidden="true"></span>
              <p class="outline-progress-tip">正在排队中，请稍等</p>
            </template>
            <template v-else>
              <div class="outline-progress-ring" aria-hidden="true">
                <svg viewBox="0 0 120 120">
                  <defs>
                    <linearGradient
                      id="outlineProgressGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stop-color="#7b4dff" />
                      <stop offset="55%" stop-color="#c45cff" />
                      <stop offset="100%" stop-color="#ff6ba8" />
                    </linearGradient>
                  </defs>
                  <circle class="outline-progress-track" cx="60" cy="60" r="52" fill="none" />
                  <circle
                    class="outline-progress-fill"
                    cx="60"
                    cy="60"
                    r="52"
                    fill="none"
                    stroke="url(#outlineProgressGradient)"
                    :stroke-dasharray="outlineProgressCircumference"
                    :stroke-dashoffset="outlineProgressOffset"
                  />
                </svg>
                <strong class="outline-progress-value">{{ Math.round(outlineProgress) }}%</strong>
              </div>
              <p class="outline-progress-tip">AI 正在编织绘本故事分镜，请稍等片刻</p>
            </template>
          </div>
          <div v-if="storyOutline" class="script-row title-row">
            <strong>标题</strong>
            <textarea
              v-model="outlineTitle"
              class="script-edit title-edit"
              rows="1"
              @input="autoResizeScriptEdit"
            ></textarea>
          </div>
          <div v-for="page in scriptPages" :key="page.no" class="script-row">
            <strong>P:{{ page.no }}</strong>
            <textarea
              v-model="page.text"
              class="script-edit"
              rows="1"
              @input="autoResizeScriptEdit"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="flow-footer">
        <button
          class="back-btn"
          type="button"
          :disabled="generating || generatingStoryboard"
          @click="currentStep = 'create'"
        >
          <span class="back-chevron">‹</span>
          <span>返回</span>
        </button>
        <button
          class="next-btn"
          type="button"
          :disabled="generating || generatingStoryboard"
          @click="handleScriptNext"
        >
          {{ generatingStoryboard ? '生成中...' : '下一步' }}
        </button>
      </div>
    </section>

    <section v-else class="style-panel">
      <div class="flow-content style-content">
        <h2 class="style-title">设置展示样式</h2>

        <div class="style-settings">
          <div class="layout-setting">
            <span class="setting-label">绘本版式</span>
            <button class="layout-card" type="button" @click="openLayoutModal">
              <img
                class="layout-card-image"
                :src="currentLayoutOption.image"
                :alt="`${currentLayoutOption.label}预览`"
              />
              <strong>{{ currentLayoutOption.label }}</strong>
              <span class="chevron">›</span>
            </button>
          </div>

          <div class="config-setting">
            <span class="setting-label">绘本配音</span>
            <button class="config-card" type="button" @click="openVoiceModal">
              <span class="voice-avatar">
                <img
                  v-if="selectedVoice.portrait"
                  :src="selectedVoice.portrait"
                  :alt="selectedVoice.name"
                  class="voice-avatar-img"
                />
              </span>
              <strong>{{ selectedVoiceLabel }}</strong>
              <span class="chevron">›</span>
            </button>

            <span class="setting-label music-label">背景音乐</span>
            <button class="config-card" type="button" @click="openMusicModal">
              <span class="play-icon">▶</span>
              <strong>{{ selectedMusicLabel }}</strong>
              <span class="chevron">›</span>
            </button>
          </div>
        </div>

        <div class="character-section">
          <h3>角色形象</h3>
          <div class="character-cards">
            <article
              v-for="character in characterCards"
              :key="character.id"
              class="character-card"
              :class="{
                'image-character': character.imageUrl,
                'text-character': !character.imageUrl
              }"
            >
              <img
                v-if="character.imageUrl"
                :src="character.imageUrl"
                :alt="`${character.name}角色形象`"
              />
              <div v-else class="empty-avatar" aria-hidden="true">
                <span></span>
              </div>
              <strong>{{ character.name }}</strong>
              <p>{{ character.description }}</p>
            </article>
          </div>
        </div>
      </div>

      <div class="flow-footer style-footer">
        <button class="back-btn" type="button" :disabled="generatingBook" @click="backToScriptStep">
          <span class="back-chevron">‹</span>
          <span>返回</span>
        </button>
        <div class="create-book-wrap">
          <div v-if="!isBookGenerated" class="pricing-info">
            <h4>定价说明</h4>
            <div class="price-row">
              <span>封面/每页插画</span>
              <strong>{{ pageUnitPrice }} 云币 / 页</strong>
            </div>
            <div class="price-row">
              <span>角色形象</span>
              <strong>{{ characterUnitPrice }} 云币 / 个</strong>
            </div>
            <div class="price-divider"></div>
            <div class="price-row total-row">
              <span>本次预计消耗</span>
              <strong>{{ estimatedTotalPrice }} 云币</strong>
            </div>
            <button
              v-if="showMemberDiscount"
              class="member-discount"
              type="button"
              @click="showUpgrade"
            >
              <img :src="upgradeVipIcon" alt="" class="vip-icon" width="16" height="16" />
              <span>会员立享7折，节省约 {{ memberDiscountSavings }} 云币</span>
            </button>
          </div>
          <button
            class="next-btn"
            type="button"
            :disabled="generatingBook || checkingBookBalance"
            @click="handleStyleFooterAction"
          >
            <span>{{ createBookButtonLabel }}</span>
            <img
              v-if="!generatingBook && !isBookGenerated"
              :src="aiCreateImage"
              alt=""
              class="btn-spark-img"
              width="12"
              height="12"
            />
            <span v-if="!generatingBook && !isBookGenerated">{{ estimatedTotalPrice }}</span>
          </button>
        </div>
      </div>
    </section>

    <div v-if="layoutModalVisible" class="layout-modal-overlay" @click.self="closeLayoutModal">
      <div class="layout-modal">
        <button
          class="layout-modal-close"
          type="button"
          aria-label="关闭"
          @click="closeLayoutModal"
        >
          ×
        </button>
        <h2 class="layout-modal-title">选择绘本版式</h2>

        <div class="layout-modal-options">
          <button
            v-for="option in layoutOptions"
            :key="option.value"
            class="layout-modal-option"
            :class="{ active: pendingLayout === option.value }"
            type="button"
            @click="selectLayoutOption(option.value)"
          >
            <div class="layout-modal-option-preview">
              <img
                class="layout-modal-option-image"
                :src="option.image"
                :alt="`${option.label}预览`"
              />
              <span
                v-if="option.membership"
                class="membership-badge membership-badge--expandable"
                :class="`membership-badge--${option.membership}`"
                :aria-label="option.membership === 'basic' ? '基础' : '高级'"
              >
                <img
                  class="membership-badge__compact"
                  :src="membershipIcons[option.membership].compact"
                  alt=""
                  aria-hidden="true"
                />
                <img
                  class="membership-badge__full"
                  :src="membershipIcons[option.membership].full"
                  alt=""
                  aria-hidden="true"
                />
              </span>
            </div>
            <span>{{ option.label }}</span>
          </button>
        </div>

        <div v-if="!isMobileViewportActive" class="layout-modal-actions">
          <button class="layout-modal-cancel" type="button" @click="closeLayoutModal">取消</button>
          <button class="layout-modal-confirm" type="button" @click="confirmLayout">确定</button>
        </div>
      </div>
    </div>

    <div
      v-if="artStyleModalVisible"
      class="art-style-modal-overlay"
      @click.self="closeArtStyleModal"
    >
      <div class="art-style-modal">
        <button
          class="art-style-modal-close"
          type="button"
          aria-label="关闭"
          @click="closeArtStyleModal"
        >
          ×
        </button>
        <h2 class="art-style-modal-title">选择绘本风格</h2>
        <!-- 
        <div class="art-style-model-tabs" role="tablist" aria-label="生图模型">
          <button
            v-for="tab in imageModelTabs"
            :key="tab.value"
            class="art-style-model-tab"
            :class="{ active: pendingImageModel === tab.value }"
            type="button"
            role="tab"
            :aria-selected="pendingImageModel === tab.value"
            @click="selectImageModelTab(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div> -->

        <div class="art-style-modal-options">
          <button
            v-for="option in pendingArtStyleOptions"
            :key="`${pendingImageModel}-${option.value}`"
            class="art-style-modal-option"
            :class="{ active: pendingArtStyle === option.value }"
            type="button"
            @click="selectArtStyleOption(option.value)"
          >
            <div class="art-style-preview" :class="option.previewClass">
              <img :src="option.url" :alt="`${option.label}风格预览`" />
              <span
                v-if="option.membership"
                class="membership-badge membership-badge--expandable"
                :class="`membership-badge--${option.membership}`"
                :aria-label="option.membership === 'basic' ? '基础' : '高级'"
              >
                <img
                  class="membership-badge__compact"
                  :src="membershipIcons[option.membership].compact"
                  alt=""
                  aria-hidden="true"
                />
                <img
                  class="membership-badge__full"
                  :src="membershipIcons[option.membership].full"
                  alt=""
                  aria-hidden="true"
                />
              </span>
            </div>
            <span>{{ option.label }}</span>
          </button>
        </div>

        <div v-if="!isMobileViewportActive" class="art-style-modal-actions">
          <button class="art-style-modal-cancel" type="button" @click="closeArtStyleModal">
            取消
          </button>
          <button class="art-style-modal-confirm" type="button" @click="confirmArtStyle">
            确定
          </button>
        </div>
      </div>
    </div>

    <VoiceSelectDialog
      v-model="voiceModalVisible"
      :initial-voice-id="selectedVoice.id"
      :initial-volume="voiceVolume"
      :initial-speed="voiceSpeed"
      @confirm="confirmVoice"
    />

    <MusicSelectDialog
      v-model="musicModalVisible"
      :initial-volume="musicVolume"
      :initial-content-url="selectedMusicUrl"
      @confirm="confirmMusic"
    />

    <CharacterEditorDialog
      v-model:visible="characterDialogVisible"
      v-model:appearance-tab="characterAppearanceTab"
      :form="characterForm"
      :is-edit="false"
      :saving="characterSaving"
      :generating="characterGenerating"
      :generation-step="characterGenerationStep"
      :can-generate="canGenerateCharacter"
      :can-confirm-generation="canConfirmCharacterGeneration"
      :show-membership-upgrade="showCharacterMembershipUpgrade"
      :generation-cost="characterUnitPrice"
      @save="handleCharacterSave"
      @redescribe="handleRedescribeCharacter"
      @confirm="handleConfirmCharacterGeneration"
      @upgrade="showUpgrade"
      @closed="handleCharacterEditorClosed"
    />

    <StoryStorageLimitDialog
      v-model="isStorageLimitVisible"
      :membership-level="membershipLevel"
      @manage="handleManageStories"
      @upgrade="showUpgrade"
    />

    <InsufficientCoinsModal
      v-model="insufficientCoinsModalVisible"
      action-text="生成绘本"
      :required-coins="estimatedTotalPrice"
      :current-coins="balanceStore.balance"
      :member-coins="premiumTotalPrice"
      @recharge="showCoinBuy"
      @upgrade="showUpgrade"
      @closed="handleInsufficientCoinsClosed"
    />

    <PhoneBindingGuard ref="phoneBindingGuardRef" />

    <div v-if="completeServiceErrorVisible" class="generation-overlay">
      <div
        class="service-error-modal"
        :class="{ 'service-error-modal--support': shouldPromptContactSupport }"
      >
        <button
          class="modal-close"
          type="button"
          aria-label="关闭"
          @click="closeCompleteServiceError"
        >
          <SvgIcon name="modal-close" :size="11" />
        </button>
        <div class="service-error-content">
          <div
            class="service-error-icon"
            :class="{ 'service-error-icon--support': shouldPromptContactSupport }"
            aria-hidden="true"
          >
            <template v-if="shouldPromptContactSupport">
              <svg
                width="25"
                height="25"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.9375 14.6875H4.6875C5.35054 14.6875 5.98643 14.9509 6.45527 15.4197C6.92411 15.8886 7.1875 16.5245 7.1875 17.1875V20.9375C7.1875 21.6005 6.92411 22.2364 6.45527 22.7053C5.98643 23.1741 5.35054 23.4375 4.6875 23.4375H3.4375C2.77446 23.4375 2.13857 23.1741 1.66973 22.7053C1.20089 22.2364 0.9375 21.6005 0.9375 20.9375V12.1875C0.9375 9.20381 2.12276 6.34233 4.23255 4.23255C6.34233 2.12276 9.20381 0.9375 12.1875 0.9375C15.1712 0.9375 18.0327 2.12276 20.1425 4.23255C22.2522 6.34233 23.4375 9.20381 23.4375 12.1875V20.9375C23.4375 21.6005 23.1741 22.2364 22.7053 22.7053C22.2364 23.1741 21.6005 23.4375 20.9375 23.4375H19.6875C19.0245 23.4375 18.3886 23.1741 17.9197 22.7053C17.4509 22.2364 17.1875 21.6005 17.1875 20.9375V17.1875C17.1875 16.5245 17.4509 15.8886 17.9197 15.4197C18.3886 14.9509 19.0245 14.6875 19.6875 14.6875H23.4375"
                  stroke="#6033E1"
                  stroke-width="1.875"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
            <template v-else>!</template>
          </div>
          <h2 class="service-error-title">
            {{ shouldPromptContactSupport ? '生成遇到问题' : completeServiceErrorContent.title }}
          </h2>
          <p class="service-error-message">
            {{
              shouldPromptContactSupport
                ? '抱歉给您带来不便，请联系客服帮助'
                : completeServiceErrorContent.message
            }}
          </p>
          <div
            class="service-error-actions"
            :class="{ 'service-error-actions--support': shouldPromptContactSupport }"
          >
            <template v-if="shouldPromptContactSupport">
              <button
                type="button"
                class="service-error-action secondary"
                :disabled="generatingBook"
                @click="retryServiceError"
              >
                {{ generatingBook ? '重试中...' : '继续重试' }}
              </button>
              <div class="service-error-action-wrap">
                <el-popover
                  placement="top"
                  :width="180"
                  :show-arrow="false"
                  :show-after="100"
                  :hide-after="100"
                  :enterable="true"
                  :trigger="isMobileViewportActive ? 'click' : 'hover'"
                  popper-class="create-support-qr-popper"
                >
                  <template #reference>
                    <button type="button" class="service-error-action primary">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.26659 13.3347C6.53897 13.9874 8.00264 14.1642 9.39384 13.8332C10.785 13.5022 12.0123 12.6852 12.8544 11.5295C13.6966 10.3737 14.0982 8.95514 13.987 7.52944C13.8758 6.10374 13.259 4.76465 12.2478 3.75346C11.2366 2.74228 9.89751 2.1255 8.47181 2.01428C7.04611 1.90305 5.62757 2.3047 4.4718 3.14683C3.31603 3.98896 2.49905 5.21621 2.16807 6.60741C1.83709 7.99861 2.01388 9.46228 2.66659 10.7347L1.33325 14.668L5.26659 13.3347Z"
                          stroke="white"
                          stroke-width="1.33333"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      <span>联系客服</span>
                    </button>
                  </template>
                  <div class="create-support-qr">
                    <img :src="createSupportQrcode" alt="技术客服二维码" />
                    <p>微信扫码添加技术客服</p>
                  </div>
                </el-popover>
              </div>
            </template>
            <button
              v-else
              type="button"
              class="service-error-action primary"
              :disabled="generatingBook"
              @click="retryServiceError"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <g clip-path="url(#clip0_2334_1569)">
                  <path
                    d="M11.2667 8.13333C11.2667 9.14861 10.9656 10.1411 10.4015 10.9853C9.83749 11.8294 9.03577 12.4874 8.09778 12.8759C7.15978 13.2644 6.12764 13.3661 5.13187 13.168C4.1361 12.97 3.22143 12.4811 2.50352 11.7631C1.78561 11.0452 1.29671 10.1306 1.09864 9.1348C0.900567 8.13903 1.00222 7.10689 1.39075 6.16889C1.77928 5.2309 2.43724 4.42918 3.28141 3.86512C4.12558 3.30106 5.11806 3 6.13334 3"
                    stroke="white"
                    stroke-width="1.68"
                    stroke-linecap="round"
                  />
                  <path
                    d="M6.56784 5.60589L8.11701 2.69232L5.20344 1.14314"
                    stroke="white"
                    stroke-width="1.68"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_2334_1569">
                    <rect width="14" height="14" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <span>{{ generatingBook ? '重试中...' : '重新尝试' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="generationModalVisible" class="generation-overlay">
      <div class="generation-modal" :class="generationModalBgClass">
        <button
          v-if="generationPhase !== 'complete'"
          class="modal-close"
          type="button"
          aria-label="关闭"
          @click="closeGenerationModal"
        >
          ×
        </button>
        <template v-if="generationPhase !== 'done'">
          <div class="generating-content">
            <h2 class="generation-title">
              <template v-if="isGenerationQueued">{{ generationModalContent.title }}</template>
              <template v-else>
                {{ generationModalContent.title }}... ({{ generationProgress }}%)
              </template>
            </h2>
            <p class="generation-subtitle">{{ generationModalContent.subtitle }}</p>
            <img
              class="generation-gif"
              :src="generationModalContent.image"
              :alt="generationModalContent.alt"
            />

            <div class="progress-wrap">
              <div class="progress-track">
                <div class="progress-fill" :style="{ width: `${generationProgress}%` }"></div>
              </div>
            </div>

            <p v-if="generationModalContent.tipText" class="generation-tips">
              <img class="generation-tip-icon" :src="tipIcon" alt="" width="18" height="18" />
              <span>{{ generationModalContent.tipText }}</span>
              <button v-if="generationModalContent.tipAction" type="button" @click="goStoryList">
                {{ generationModalContent.tipAction }}
              </button>
              <span v-if="generationModalContent.tipAction">查看</span>
            </p>
          </div>
        </template>

        <template v-else-if="generationTtsStatus === 'success'">
          <div class="generation-done-content generation-done-success">
            <div class="done-title-row">
              <span class="done-check">✓</span>
              <h2>作品已全部生成，快去阅读吧！</h2>
            </div>

            <img class="done-story-image" :src="doneStoryImage" alt="绘本生成完成" />

            <div class="modal-actions">
              <button type="button" class="modal-action secondary" @click="goEditBook">
                编辑或分享绘本
              </button>
              <button type="button" class="modal-action primary" @click="goReadBook">
                阅读绘本
              </button>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="generation-done-content generation-done-partial">
            <h2 class="done-partial-title">绘本已生成</h2>
            <p class="done-partial-subtitle">
              {{
                generationTtsStatus === 'partial-failed'
                  ? '插画生成成功，配音部分合成失败'
                  : '插画生成成功，配音合成失败'
              }}
            </p>

            <div class="done-status-list">
              <div class="done-status-card success">
                <span class="done-status-icon success" aria-hidden="true">✓</span>
                <span class="done-status-text">绘本插画生成成功</span>
              </div>

              <div class="done-status-card warning">
                <span class="done-status-icon warning" aria-hidden="true">!</span>
                <div class="done-status-body">
                  <template v-if="ttsRetrying">
                    <strong>合成中...</strong>
                  </template>
                  <template v-else>
                    <strong>
                      {{
                        generationTtsStatus === 'partial-failed'
                          ? '部分配音合成失败'
                          : '配音合成失败'
                      }}
                    </strong>
                    <p>可进入编辑绘本重新合成，不影响阅读</p>
                  </template>
                </div>
                <button
                  v-if="!ttsRetrying"
                  type="button"
                  class="done-tts-retry"
                  @click="retryFailedTts"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <g clip-path="url(#clip_tts_retry)">
                      <path
                        d="M11.2667 8.13333C11.2667 9.14861 10.9656 10.1411 10.4015 10.9853C9.83749 11.8294 9.03577 12.4874 8.09778 12.8759C7.15978 13.2644 6.12764 13.3661 5.13187 13.168C4.1361 12.97 3.22143 12.4811 2.50352 11.7631C1.78561 11.0452 1.29671 10.1306 1.09864 9.1348C0.900567 8.13903 1.00222 7.10689 1.39075 6.16889C1.77928 5.2309 2.43724 4.42918 3.28141 3.86512C4.12558 3.30106 5.11806 3 6.13334 3"
                        stroke="currentColor"
                        stroke-width="1.68"
                        stroke-linecap="round"
                      />
                      <path
                        d="M6.56784 5.60589L8.11701 2.69232L5.20344 1.14314"
                        stroke="currentColor"
                        stroke-width="1.68"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip_tts_retry">
                        <rect width="14" height="14" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span>重试</span>
                </button>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" class="modal-action secondary" @click="goEditBook">
                编辑绘本
              </button>
              <button type="button" class="modal-action primary" @click="goReadBook">
                阅读绘本
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
    <EditorPreviewModal :visible="previewVisible" @close="previewVisible = false" />
    <CreateOnboardingGuide v-if="onboardingVisible" @close="completeOnboarding" />
  </div>
</template>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onDeactivated,
    onMounted,
    reactive,
    ref,
    watch
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { ElMessage } from 'element-plus';
  import { showLogin } from '@/utils/login';
  import { useUserStore } from '@/store';
  import { useCoinGift, useUpgradeModal } from '@/hooks';
  import {
    setOnboardingDialogOpen,
    useDashboardDialogCoordinator
  } from '@/hooks/useDashboardDialogCoordinator';
  import { projectManager } from '@/managers';
  import EditorPreviewModal from '@/views/editor/components/EditorPreviewModal.vue';
  import crossPage11Image from '@/assets/layout-options/cross_page_1_1.png';
  import crossPage34Image from '@/assets/layout-options/cross_page_3_4.png';
  import fullImage169 from '@/assets/layout-options/full_image_16_9.png';
  import imageTextV11Image from '@/assets/layout-options/image_text_v_1_1.png';
  import imageTextV34Image from '@/assets/layout-options/image_text_v_3_4.png';
  import textImageH34Image from '@/assets/layout-options/text_image_h_3_4.png';
  import aiCreateImage from '@/assets/images/ai-create.png';
  import completeStoryGif from '@/assets/images/complete-story.gif';
  import doneStoryImage from '@/assets/images/done-story.png';
  import generateImagesGif from '@/assets/images/generate-images.gif';
  import tipIcon from '@/assets/images/tip.png';
  import createSupportQrcode from '@/assets/images/create-support-qrcode.png';
  import upgradeVipIcon from '@/assets/icons/upgrade-vip.png';
  import cartoonAnime4Image from '@/assets/images/style/cartoon-anime-4.webp';
  import chineseAnimation1Image from '@/assets/images/style/chinese-animation-1.webp';
  import clayStopMotion12Image from '@/assets/images/style/clay-stop-motion-12.webp';
  import crayonDoodle6Image from '@/assets/images/style/crayon-doodle-6.webp';
  import forestFairyTale3Image from '@/assets/images/style/forest-fairy-tale-3.webp';
  import inkChinese5Image from '@/assets/images/style/ink-chinese-5.webp';
  import japaneseHealing11Image from '@/assets/images/style/japanese-healing-11.webp';
  import oilImpasto9Image from '@/assets/images/style/oil-impasto-9.webp';
  import paperCollage10Image from '@/assets/images/style/paper-collage-10.webp';
  import pastelSoft2Image from '@/assets/images/style/pastel-soft-2.webp';
  import qFlat8Image from '@/assets/images/style/q-flat-8.webp';
  import watercolorFairyTale7Image from '@/assets/images/style/watercolor-fairy-tale-7.webp';
  import huoshanCartoonSimpleImage from '@/assets/images/style/huoshan/cartoon-simple.webp';
  import huoshanChinese3dImage from '@/assets/images/style/huoshan/chinese-3d.webp';
  import huoshanChineseAnimationImage from '@/assets/images/style/huoshan/chinese-animation.webp';
  import huoshanClayStopMotionImage from '@/assets/images/style/huoshan/clay-stop-motion.webp';
  import huoshanCrayonDoodleImage from '@/assets/images/style/huoshan/crayon-doodle.webp';
  import huoshanForestFairyTaleImage from '@/assets/images/style/huoshan/forest-fairy-tale.webp';
  import huoshanInkChineseImage from '@/assets/images/style/huoshan/ink-chinese.webp';
  import huoshanJapaneseHandDrawnImage from '@/assets/images/style/huoshan/japanese-hand-drawn.webp';
  import huoshanOilImpastoImage from '@/assets/images/style/huoshan/oil-impasto.webp';
  import huoshanPaperCollageImage from '@/assets/images/style/huoshan/paper-collage.webp';
  import huoshanPastelSoftImage from '@/assets/images/style/huoshan/pastel-soft.webp';
  import huoshanQ3dImage from '@/assets/images/style/huoshan/q-3d.webp';
  import huoshanQFlatImage from '@/assets/images/style/huoshan/q-flat.webp';
  import huoshanWatercolorFairyTaleImage from '@/assets/images/style/huoshan/watercolor-fairy-tale.webp';
  import createMembershipBasicIcon from '@/assets/icons/svg/create-membership-basic.svg?url';
  import createMembershipBasicFullIcon from '@/assets/icons/svg/create-membership-basic-full.svg?url';
  import createMembershipPremiumIcon from '@/assets/icons/svg/create-membership-premium.svg?url';
  import createMembershipPremiumFullIcon from '@/assets/icons/svg/create-membership-premium-full.svg?url';
  import createGenerateSparkIcon from '@/assets/icons/svg/create-generate-spark.svg?url';
  import { hasMembershipAccess } from '@/api/user';
  import { deleteStory, fetchStoryList } from '@/api/story';
  import { getStoryStorageLimit, normalizeStoryMembershipLevel } from '@/utils/storyStorage';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import StoryStorageLimitDialog from '@/components/common/StoryStorageLimitDialog.vue';
  import InsufficientCoinsModal from '@/components/common/InsufficientCoinsModal.vue';
  import PhoneBindingGuard from '@/components/common/PhoneBindingGuard.vue';
  import CreateOptionSelect from './CreateOptionSelect.vue';
  import PromptMentionEditor from './PromptMentionEditor.vue';
  import CreateOnboardingGuide from './CreateOnboardingGuide.vue';
  import VoiceSelectDialog, {
    type VoiceOption,
    type VoiceSelectResult
  } from './VoiceSelectDialog.vue';
  // import { createPageErrorEditWindowName } from '@/views/book/pageErrorEditAccess';
  import MusicSelectDialog from '@/views/editor/components/tool/MusicSelectDialog.vue';
  import type { MusicSelectResult } from '@/views/editor/components/tool/MusicSelectDialog.vue';
  import CharacterEditorDialog from '@/views/manage/character/components/CharacterEditorDialog.vue';
  import {
    completeStoryFromOutline,
    createStoryOutline,
    pollStoryProgress,
    pollTask,
    previewStoryCost,
    repairStoryOutline,
    retryStory,
    type BookLayout,
    type ImageGenerateResult,
    type ImagePhase,
    type ImageProvider,
    type StoryAppearance,
    type StoryBook,
    type StoryCharacter,
    type StoryCompleteMusicPayload,
    type StoryCreatePayload,
    type InputCharacterRef,
    type StoryOutline,
    type StoryOutlineCreatePayload,
    type StoryOutlineRepairResult,
    type StoryOutlineResult,
    type StoryProgressSnapshot
  } from '@/api/storybook.ts';
  import {
    createCharacter,
    fetchCharacterList,
    getCharacter,
    resolveCharacterTaskResult,
    updateCharacter,
    waitForCharacterTask,
    type CharacterItem
  } from '@/api/character';
  import { CHARACTER_DEFAULT_ART_STYLE } from '@/constants/characterArtStyles';
  import {
    IMAGE_GENERATION_PRICE_BY_LEVEL,
    normalizeImageGenerationPriceLevel
  } from '@/constants/imageGenerationPricing';
  import { useBalanceStore } from '@/store';
  import {
    submitCreateTts,
    fetchDefaultTtsVoice,
    type CreateTtsPageInput,
    type CreateTtsPayload,
    type CreateTtsResult,
    type TtsAudioItem,
    type TtsTaskResult
  } from '@/api/tts';
  import { prepareBookForRead } from '@/modules/editor/canvas/models/template/kieStoryParser';

  type OptionKey = 'readerAge' | 'pageCount' | 'artStyle' | 'language' | 'narrator';
  type Step = 'create' | 'script' | 'style';
  type GenerationPhase = 'complete' | 'images' | 'voice' | 'organizing' | 'done';
  type CharacterAppearanceTab = 'text' | 'image';
  type CharacterGenerationStep = 'form' | 'preview';
  type LayoutValue =
    | 'full_image_16_9'
    | 'cross_page_1_1'
    | 'cross_page_3_4'
    | 'image_text_v_1_1'
    | 'image_text_v_3_4'
    | 'text_image_h_3_4';
  type PageAspectRatio = '16:9' | '1:1' | '3:4';
  type CharacterAspectRatio = '1:1' | '3:4';
  type CoverAspectRatio = '4:5' | '3:4' | '1:1';
  type CompleteServiceErrorKind = 'busy' | 'network';
  type ServiceErrorSource = 'complete' | 'images';
  type GenerationTtsStatus = 'success' | 'all-failed' | 'partial-failed';
  type ImageModelKind = 'fast' | 'fine';

  interface PendingBookGeneration {
    storyForImages: StoryBook;
    projectId: string | number | undefined;
    phases: ImagePhase[];
    imageLayout: BookLayout;
    imageAspectRatio: string;
    imageCharacterAspectRatio: string;
    imageCoverAspectRatio: string;
    imageProvider: ImageProvider;
  }

  interface TtsRetryContext {
    payload: CreateTtsPayload;
    /** 与 payload.pages 等长，成功项含 url，失败项为空占位 */
    audio: TtsAudioItem[];
  }

  interface ScriptPage {
    no: string;
    text: string;
    originalText: string;
    page: number;
  }

  interface CharacterCard {
    id: string;
    name: string;
    description: string;
    imageUrl: string;
  }

  type CharacterForm = Omit<CharacterItem, 'id' | 'createdAt' | 'updatedAt'> & { id?: string };

  type CreateMembership = 'basic' | 'premium';

  interface LayoutOption {
    value: LayoutValue;
    label: string;
    previewClass: string;
    image: string;
    page_aspect_ratio: PageAspectRatio;
    character_aspect_ratio: CharacterAspectRatio;
    cover_aspect_ratio: CoverAspectRatio;
    /** 版式会员门槛：全屏单画、上图下文(3:4) 免费不传，其余 basic */
    membership?: CreateMembership;
  }

  interface ArtStyleOption {
    value: string;
    label: string;
    previewClass: string;
    url: string;
    /** 风格会员门槛：1-3 免费不传，4-7 basic，8-12 premium */
    membership?: CreateMembership;
  }

  const membershipIcons: Record<CreateMembership, { compact: string; full: string }> = {
    basic: {
      compact: createMembershipBasicIcon,
      full: createMembershipBasicFullIcon
    },
    premium: {
      compact: createMembershipPremiumIcon,
      full: createMembershipPremiumFullIcon
    }
  };

  const route = useRoute();
  const router = useRouter();
  // 角色、故事和插图生成都可能消耗云币，通过全局 Store 同步顶部栏等公共区域的余额。
  const balanceStore = useBalanceStore();
  const userStore = useUserStore();
  const { coinGiftDialogOpen, dashboardDialogsReady } = useDashboardDialogCoordinator();
  const { showUpgrade, showCoinBuy } = useUpgradeModal();
  const { checkAndOpenCoinGift } = useCoinGift();

  const handleInsufficientCoinsClosed = (reason: 'cancel' | 'recharge' | 'upgrade') => {
    if (reason === 'cancel') void checkAndOpenCoinGift();
  };

  const currentStep = ref<Step>('create');
  const generating = ref(false);
  const generatingStoryboard = ref(false);
  const generatingBook = ref(false);
  const checkingBookBalance = ref(false);
  const phoneBindingGuardRef = ref<InstanceType<typeof PhoneBindingGuard>>();
  let createRequestPending = false;
  const generationModalVisible = ref(false);
  const previewVisible = ref(false);
  // 临时联调开关：忽略首次引导元数据，每次进入创作页都展示。
  const forceShowOnboarding = false;
  const onboardingVisible = ref(false);
  let onboardingDecisionPending = false;

  const completeOnboarding = (_reason: 'skip' | 'complete') => {
    onboardingVisible.value = false;
    setOnboardingDialogOpen(false);
  };

  const showOnboardingWhenReady = async () => {
    if (
      onboardingDecisionPending ||
      onboardingVisible.value ||
      route.name !== 'create' ||
      !userStore.isLogin ||
      (!forceShowOnboarding && userStore.userMeta.hasOnboarding) ||
      !dashboardDialogsReady.value ||
      coinGiftDialogOpen.value
    ) {
      return;
    }

    onboardingDecisionPending = true;
    onboardingVisible.value = true;
    setOnboardingDialogOpen(true);

    if (forceShowOnboarding) {
      onboardingDecisionPending = false;
      return;
    }

    try {
      await userStore.setMeta({ hasOnboarding: true });
    } catch (error) {
      console.error(error, 'save onboarding status error');
    } finally {
      onboardingDecisionPending = false;
    }
  };

  const layoutModalVisible = ref(false);
  const artStyleModalVisible = ref(false);
  const isStorageLimitVisible = ref(false);
  const isCheckingStorage = ref(false);
  const insufficientCoinsModalVisible = ref(false);
  const voiceModalVisible = ref(false);
  const voiceVolume = ref(75);
  const voiceSpeed = ref(1);
  const selectedVoice = ref<VoiceOption>({
    id: '',
    name: '',
    description: '',
    portrait: '',
    provider: '',
    category: '',
    profile: {
      auditionText: '',
      auditionUrl: ''
    }
  });
  const selectedVoiceLabel = computed(() => {
    if (!selectedVoice.value.id) return '请选择配音';
    return selectedVoice.value.description
      ? `${selectedVoice.value.name} | ${selectedVoice.value.description}`
      : selectedVoice.value.name;
  });
  const musicModalVisible = ref(false);
  const musicVolume = ref(0.3);
  const selectedMusicId = ref<string | null>(null);
  const selectedMusicName = ref('');
  const selectedMusicUrl = ref<string | null>(null);
  const selectedMusicLabel = computed(() => {
    if (selectedMusicId.value === null) return '不使用背景音乐';
    return selectedMusicName.value || '请选择背景音乐';
  });
  const mentionModalVisible = ref(false);
  const hasOpenedMentionPopup = ref(false);
  const mentionLoading = ref(false);
  const mentionCharacters = ref<CharacterItem[]>([]);
  const pinnedMentionCharacters = ref<CharacterItem[]>([]);
  /** 主题里已成功 @ 过的角色名，避免列表刷新后匹配不到导致计数丢失 */
  const trackedMentionNames = ref<string[]>([]);
  const selectedMentionId = ref('');
  const mentionWrapRef = ref<HTMLElement | null>(null);
  const promptEditorRef = ref<InstanceType<typeof PromptMentionEditor> | null>(null);
  const mentionPopupStyle = ref<Record<string, string>>({});
  const characterDialogVisible = ref(false);
  const characterSaving = ref(false);
  const characterGenerating = ref(false);
  const characterGenerationStep = ref<CharacterGenerationStep>('form');
  const characterGenerationRequestId = ref(0);
  const characterAppearanceTab = ref<CharacterAppearanceTab>('image');
  const pendingCreatedCharacterId = ref('');
  const showMentionPopupAfterCharacterClose = ref(false);
  let mentionCharactersLoaded = false;
  let mentionCharactersRequest: Promise<void> | null = null;
  let routeCharacterRequestId = 0;

  const initDefaultVoice = async () => {
    if (selectedVoice.value.id) return;
    try {
      const voice = await fetchDefaultTtsVoice();
      if (voice) {
        selectedVoice.value = voice;
      }
    } catch {
      // 默认配音加载失败时保留空状态，用户可手动选择
    }
  };

  const isMobileViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  const isMobileViewportActive = ref(isMobileViewport());
  /** keep-alive 下页面是否处于激活态；用于控制 Teleport 到 body 的移动端固定按钮 */
  const isCreatePageActive = ref(true);

  const promptPlaceholder = computed(() =>
    isMobileViewportActive.value
      ? '输入灵感,  创作你的绘本故事！'
      : '请输入绘本主题内容，支持@引用角色，自定义主人公形象。例如：一只小兔子@聪聪 在森林里寻找彩虹的故事。'
  );

  const MENTION_POPUP_WIDTH = 429;
  const MENTION_POPUP_HEIGHT = 436;
  const MENTION_POPUP_GAP = 10;
  const MENTION_POPUP_MARGIN = 12;
  /** 主题输入中 @ 引用角色上限 */
  const MAX_MENTION_CHARACTERS = 5;
  const selectedLayout = ref<LayoutValue>('full_image_16_9');
  const pendingLayout = ref<LayoutValue>('full_image_16_9');
  const pendingArtStyle = ref('水彩童话');
  const generationDone = ref(false);
  const generationPhase = ref<GenerationPhase>('complete');
  const generationProgress = ref(0);
  const generationTtsStatus = ref<GenerationTtsStatus>('success');
  const ttsRetrying = ref(false);
  const ttsRetryContext = ref<TtsRetryContext | null>(null);
  const pendingBookGeneration = ref<PendingBookGeneration | null>(null);
  const completeServiceErrorVisible = ref(false);
  const completeServiceErrorKind = ref<CompleteServiceErrorKind>('busy');
  const serviceErrorSource = ref<ServiceErrorSource>('complete');
  /** 第三步创作失败后的重试次数；达到阈值后引导联系客服 */
  const createBookRetryCount = ref(0);
  const CREATE_BOOK_RETRY_CONTACT_THRESHOLD = 3;
  const shouldPromptContactSupport = computed(
    () => createBookRetryCount.value >= CREATE_BOOK_RETRY_CONTACT_THRESHOLD
  );
  /** 上一次 /complete 提交返回的工程 id，重试前用于清理 */
  const pendingCompleteProjectId = ref<string | number>();
  /** /get-progress queued 时按 stage 切换排队文案 */
  const isCompleteQueued = ref(false);
  const isImagesQueued = ref(false);
  const isOrganizingQueued = ref(false);
  const isGenerationQueued = computed(
    () =>
      (generationPhase.value === 'complete' && isCompleteQueued.value) ||
      (generationPhase.value === 'images' && isImagesQueued.value) ||
      (generationPhase.value === 'organizing' && isOrganizingQueued.value)
  );

  const story = ref<StoryBook | null>(null);
  const storyProjectId = ref<string | number>();
  const storyOutline = ref<StoryOutline | null>(null);
  const outlineTitle = ref('');
  const outlineProgress = ref(0);
  /** task-progress 返回 queued 时不展示进度百分比，仅转圈等待 */
  const isOutlineQueued = ref(false);
  const OUTLINE_PROGRESS_RADIUS = 52;
  const OUTLINE_FAKE_PROGRESS_MS = 50_000;
  const OUTLINE_FAKE_PROGRESS_CAP = 99;
  const outlineProgressCircumference = 2 * Math.PI * OUTLINE_PROGRESS_RADIUS;
  const outlineProgressOffset = computed(
    () =>
      outlineProgressCircumference * (1 - Math.max(0, Math.min(100, outlineProgress.value)) / 100)
  );
  let outlineFakeProgressTimer: ReturnType<typeof setInterval> | null = null;
  let outlineFakeProgressStartedAt = 0;

  const stopOutlineFakeProgress = (options?: { complete?: boolean }) => {
    if (outlineFakeProgressTimer) {
      clearInterval(outlineFakeProgressTimer);
      outlineFakeProgressTimer = null;
    }
    outlineFakeProgressStartedAt = 0;
    if (options?.complete) {
      outlineProgress.value = 100;
    }
  };

  /** 约50秒从 0 逼近 99%，永不真正到 100，接口完成后由 stopOutlineFakeProgress 拉满 */
  const startOutlineFakeProgress = () => {
    stopOutlineFakeProgress();
    outlineProgress.value = 0;
    outlineFakeProgressStartedAt = Date.now();
    outlineFakeProgressTimer = setInterval(() => {
      const elapsed = Date.now() - outlineFakeProgressStartedAt;
      const ratio = elapsed / OUTLINE_FAKE_PROGRESS_MS;
      outlineProgress.value = OUTLINE_FAKE_PROGRESS_CAP * (1 - Math.exp(-3 * ratio));
    }, 200);
  };

  const handleOutlineTaskUpdate = (status: string) => {
    if (status === 'queued') {
      isOutlineQueued.value = true;
      stopOutlineFakeProgress();
      outlineProgress.value = 0;
      return;
    }

    if (isOutlineQueued.value) {
      isOutlineQueued.value = false;
      startOutlineFakeProgress();
    }
  };

  const storyRequest = ref<StoryCreatePayload | null>(null);
  const finalImageResult = ref<ImageGenerateResult | null>(null);
  const workULink = ref<string>();
  const workLinkName = ref<string>();

  const form = ref<Record<OptionKey, string> & { description: string }>({
    description: '',
    readerAge: '7-9岁',
    pageCount: '5-10',
    artStyle: '国风动画',
    language: 'zh',
    narrator: 'third'
  });

  const emptyCharacterForm = (): CharacterForm => ({
    name: '',
    age: undefined,
    gender: '',
    personality: [],
    catchphrase: '',
    artStyle: CHARACTER_DEFAULT_ART_STYLE,
    avatarUrl: '',
    generatedUrl: '',
    refImageUrl: '',
    description: '',
    setting: ''
  });

  const characterForm = reactive<CharacterForm>(emptyCharacterForm());

  const layoutOptions: LayoutOption[] = [
    {
      value: 'full_image_16_9',
      label: '全屏单画',
      previewClass: 'full-image',
      image: fullImage169,
      page_aspect_ratio: '16:9',
      character_aspect_ratio: '3:4',
      cover_aspect_ratio: '4:5'
    },
    {
      value: 'image_text_v_3_4',
      label: '上图下文(3:4)',
      previewClass: 'text-left',
      image: imageTextV34Image,
      page_aspect_ratio: '3:4',
      character_aspect_ratio: '3:4',
      cover_aspect_ratio: '3:4'
    },
    {
      value: 'cross_page_3_4',
      label: '跨页双画(3:4)',
      previewClass: 'left-right',
      image: crossPage34Image,
      page_aspect_ratio: '3:4',
      character_aspect_ratio: '3:4',
      cover_aspect_ratio: '3:4',
      membership: 'basic'
    },
    {
      value: 'image_text_v_1_1',
      label: '上图下文(1:1)',
      previewClass: 'text-left',
      image: imageTextV11Image,
      page_aspect_ratio: '1:1',
      character_aspect_ratio: '3:4',
      cover_aspect_ratio: '1:1',
      membership: 'basic'
    },
    {
      value: 'text_image_h_3_4',
      label: '左文右图',
      previewClass: 'text-left',
      image: textImageH34Image,
      page_aspect_ratio: '3:4',
      character_aspect_ratio: '3:4',
      cover_aspect_ratio: '3:4',
      membership: 'basic'
    },
    {
      value: 'cross_page_1_1',
      label: '跨页双画(1:1)',
      previewClass: 'cross-page',
      image: crossPage11Image,
      page_aspect_ratio: '1:1',
      character_aspect_ratio: '3:4',
      cover_aspect_ratio: '1:1',
      membership: 'basic'
    }
  ];

  const currentLayoutOption = computed(
    () => layoutOptions.find((option) => option.value === selectedLayout.value) ?? layoutOptions[0]
  );

  const imageModelTabs: Array<{
    value: ImageModelKind;
    provider: ImageProvider;
    label: string;
  }> = [
    {
      value: 'fast',
      provider: 'huoshan',
      label: '快速模型（轻量智绘,生图约2-3分钟）'
    }
    // {
    //   value: 'fine',
    //   provider: 'kie',
    //   label: '精细模型（细节丰富, 5-8分钟）'
    // }
  ];

  /** 快速模型（huoshan）风格列表 */
  const fastArtStyleOptions: ArtStyleOption[] = [
    {
      value: '国风动画',
      label: '国风动画',
      previewClass: 'chinese-animation',
      url: huoshanChineseAnimationImage
    },
    {
      value: '水彩童话',
      label: '水彩童话',
      previewClass: 'watercolor',
      url: huoshanWatercolorFairyTaleImage
    },
    {
      value: '蜡笔涂鸦',
      label: '蜡笔涂鸦',
      previewClass: 'crayon',
      url: huoshanCrayonDoodleImage
    },
    {
      value: '卡通简约',
      label: '卡通简约',
      previewClass: 'cartoon',
      url: huoshanCartoonSimpleImage
    },
    {
      value: '拼贴纸艺',
      label: '拼贴纸艺',
      previewClass: 'paper',
      url: huoshanPaperCollageImage
    },
    {
      value: '水墨国风',
      label: '水墨国风',
      previewClass: 'ink',
      url: huoshanInkChineseImage,
      membership: 'basic'
    },
    {
      value: '国风3D',
      label: '国风3D',
      previewClass: 'chinese-animation',
      url: huoshanChinese3dImage,
      membership: 'basic'
    },
    {
      value: '森林童话',
      label: '森林童话',
      previewClass: 'forest',
      url: huoshanForestFairyTaleImage,
      membership: 'basic'
    },
    {
      value: '扁平几何',
      label: '扁平几何',
      previewClass: 'flat',
      url: huoshanQFlatImage,
      membership: 'basic'
    },
    {
      value: '粉彩柔雾',
      label: '粉彩柔雾',
      previewClass: 'pastel',
      url: huoshanPastelSoftImage,
      membership: 'premium'
    },
    {
      value: '日式手绘',
      label: '日式手绘',
      previewClass: 'japanese',
      url: huoshanJapaneseHandDrawnImage,
      membership: 'premium'
    },
    {
      value: 'Q版3D',
      label: 'Q版3D',
      previewClass: 'flat',
      url: huoshanQ3dImage,
      membership: 'premium'
    },
    {
      value: '油画厚涂',
      label: '油画厚涂',
      previewClass: 'oil',
      url: huoshanOilImpastoImage,
      membership: 'premium'
    },
    {
      value: '黏土定格',
      label: '黏土定格',
      previewClass: 'clay',
      url: huoshanClayStopMotionImage,
      membership: 'premium'
    }
  ];

  /** 精细模型（kie）风格列表，可独立配置预览图 */
  const fineArtStyleOptions: ArtStyleOption[] = [
    {
      value: '国风动画',
      label: '国风动画',
      previewClass: 'chinese-animation',
      url: chineseAnimation1Image
    },
    {
      value: '水彩童话',
      label: '水彩童话',
      previewClass: 'watercolor',
      url: watercolorFairyTale7Image
    },
    {
      value: '蜡笔涂鸦',
      label: '蜡笔涂鸦',
      previewClass: 'crayon',
      url: crayonDoodle6Image
    },
    {
      value: '卡通简约',
      label: '卡通简约',
      previewClass: 'cartoon',
      url: cartoonAnime4Image
    },
    {
      value: '拼贴纸艺',
      label: '拼贴纸艺',
      previewClass: 'paper',
      url: paperCollage10Image
    },
    {
      value: '水墨国风',
      label: '水墨国风',
      previewClass: 'ink',
      url: inkChinese5Image,
      membership: 'basic'
    },
    {
      value: '国风3D',
      label: '国风3D',
      previewClass: 'chinese-animation',
      url: chineseAnimation1Image,
      membership: 'basic'
    },
    {
      value: '森林童话',
      label: '森林童话',
      previewClass: 'forest',
      url: forestFairyTale3Image,
      membership: 'basic'
    },
    {
      value: '扁平几何',
      label: '扁平几何',
      previewClass: 'flat',
      url: qFlat8Image,
      membership: 'basic'
    },
    {
      value: '粉彩柔雾',
      label: '粉彩柔雾',
      previewClass: 'pastel',
      url: pastelSoft2Image
      // membership: 'premium'
    },
    {
      value: '日式手绘',
      label: '日式手绘',
      previewClass: 'japanese',
      url: japaneseHealing11Image,
      membership: 'premium'
    },
    {
      value: 'Q版3D',
      label: 'Q版3D',
      previewClass: 'flat',
      url: qFlat8Image,
      membership: 'premium'
    },
    {
      value: '油画厚涂',
      label: '油画厚涂',
      previewClass: 'oil',
      url: oilImpasto9Image,
      membership: 'premium'
    },
    {
      value: '黏土定格',
      label: '黏土定格',
      previewClass: 'clay',
      url: clayStopMotion12Image,
      membership: 'premium'
    }
  ];

  const artStylesByModel: Record<ImageModelKind, ArtStyleOption[]> = {
    fast: fastArtStyleOptions,
    fine: fineArtStyleOptions
  };

  const selectedImageModel = ref<ImageModelKind>('fast');
  const pendingImageModel = ref<ImageModelKind>('fast');
  const pendingArtStyleOptions = computed(() => artStylesByModel[pendingImageModel.value]);
  const selectedImageProvider = computed(
    () =>
      imageModelTabs.find((tab) => tab.value === selectedImageModel.value)?.provider ?? 'huoshan'
  );

  const currentArtStyleOption = computed(() => {
    const options = artStylesByModel[selectedImageModel.value];
    return options.find((option) => option.value === form.value.artStyle) ?? options[0];
  });

  const isBookGenerated = computed(
    () =>
      generationDone.value ||
      Boolean(finalImageResult.value?.story || finalImageResult.value?.cover?.image_url)
  );

  const createBookButtonLabel = computed(() => {
    if (generatingBook.value) return '生成中...';
    if (isBookGenerated.value) return '创作新绘本';
    return '生成绘本';
  });

  const canGenerateCharacter = computed(() => {
    const hasAppearance =
      characterAppearanceTab.value === 'text'
        ? characterForm.description.trim()
        : Boolean(characterForm.refImageUrl);

    return Boolean(characterForm.artStyle && hasAppearance);
  });

  const canConfirmCharacterGeneration = computed(() =>
    Boolean(characterForm.id && characterForm.name.trim() && characterForm.setting.trim())
  );

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'object' && error !== null) {
      const record = error as Record<string, unknown>;
      // 接口 reject 常见字段：msg / errorMsg
      if (typeof record.msg === 'string' && record.msg) return record.msg;
      if (typeof record.errorMsg === 'string' && record.errorMsg) return record.errorMsg;
      if (typeof record.message === 'string') return record.message;
      if (typeof record.error === 'string') return record.error;
      const response = record.response as Record<string, unknown> | undefined;
      const data = response?.data as Record<string, unknown> | undefined;
      if (typeof data?.message === 'string') return data.message;
      if (typeof data?.msg === 'string') return data.msg;
      if (typeof data?.error === 'string') return data.error;
    }
    return fallback;
  };

  const getHttpStatus = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') return undefined;
    const response = (error as { response?: { status?: number } }).response;
    return response?.status;
  };

  const getAxiosCode = (error: unknown): string | undefined => {
    if (!error || typeof error !== 'object') return undefined;
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  };

  /** 读取接口业务错误码（如 REACH_LIMIT / INSUFFICIENT_BALANCE） */
  const getApiBusinessCode = (error: unknown): string | undefined => {
    if (!error || typeof error !== 'object') return undefined;
    const record = error as Record<string, unknown>;

    const response = record.response as Record<string, unknown> | undefined;
    const data = response?.data as Record<string, unknown> | undefined;
    if (typeof data?.code === 'string' && data.code && data.code !== 'OK') {
      return data.code;
    }

    if (typeof record.code === 'string' && record.code) {
      // 忽略 axios 传输层错误码
      if (
        record.code.startsWith('ERR_') ||
        record.code === 'ECONNABORTED' ||
        record.code === 'ETIMEDOUT'
      ) {
        return undefined;
      }
      return record.code;
    }

    return undefined;
  };

  const TEXT_SCAN_FAILED_MESSAGE = '输入内容审核不合规';

  const isTextScanFailedError = (error: unknown) =>
    getApiBusinessCode(error) === 'TEXT_SCAN_FAILED';

  const showTextScanFailedError = () => {
    ElMessage.error(TEXT_SCAN_FAILED_MESSAGE);
  };

  type CompleteLimitErrorCode = 'REACH_LIMIT' | 'INSUFFICIENT_BALANCE';

  const createCompleteLimitError = (code: CompleteLimitErrorCode, message?: string) => {
    const error = new Error(message || '') as Error & {
      stage: 'complete-limit';
      code: CompleteLimitErrorCode;
    };
    error.stage = 'complete-limit';
    error.code = code;
    return error;
  };

  const isCompleteLimitError = (
    error: unknown
  ): error is Error & { stage: 'complete-limit'; code: CompleteLimitErrorCode } =>
    Boolean(
      error &&
      typeof error === 'object' &&
      'stage' in error &&
      (error as { stage?: string }).stage === 'complete-limit' &&
      'code' in error &&
      ((error as { code?: string }).code === 'REACH_LIMIT' ||
        (error as { code?: string }).code === 'INSUFFICIENT_BALANCE')
    );

  const createLoginExpiredError = (message?: string) => {
    const error = new Error(message || '用户登录已过期') as Error & {
      stage: 'login-expired';
      code: 'LOGIN_TIMEOUT';
    };
    error.stage = 'login-expired';
    error.code = 'LOGIN_TIMEOUT';
    return error;
  };

  const isLoginExpiredError = (
    error: unknown
  ): error is Error & { stage: 'login-expired'; code: 'LOGIN_TIMEOUT' } =>
    Boolean(
      error &&
      typeof error === 'object' &&
      'stage' in error &&
      (error as { stage?: string }).stage === 'login-expired'
    );

  const isTimeoutLikeMessage = (message: string) =>
    /超时|timeout|timed\s*out|econnaborted|etimedout|gateway time-?out|504/i.test(message);

  const isNetworkLikeMessage = (message: string) =>
    /network\s*error|net::err_|failed to fetch|err_internet_disconnected|网络/i.test(message);

  const createCompleteServiceError = (kind: CompleteServiceErrorKind, message?: string) => {
    const error = new Error(message || '') as Error & {
      stage: 'complete-service';
      kind: CompleteServiceErrorKind;
    };
    error.stage = 'complete-service';
    error.kind = kind;
    return error;
  };

  // const createImageServiceError = (kind: CompleteServiceErrorKind, message?: string) => {
  //   const error = new Error(message || '') as Error & {
  //     stage: 'images-service';
  //     kind: CompleteServiceErrorKind;
  //   };
  //   error.stage = 'images-service';
  //   error.kind = kind;
  //   return error;
  // };

  const isCompleteServiceError = (
    error: unknown
  ): error is Error & { stage: 'complete-service'; kind: CompleteServiceErrorKind } =>
    Boolean(
      error &&
      typeof error === 'object' &&
      'stage' in error &&
      (error as { stage?: string }).stage === 'complete-service' &&
      'kind' in error &&
      ((error as { kind?: CompleteServiceErrorKind }).kind === 'busy' ||
        (error as { kind?: CompleteServiceErrorKind }).kind === 'network')
    );

  const isImageServiceError = (
    error: unknown
  ): error is Error & { stage: 'images-service'; kind: CompleteServiceErrorKind } =>
    Boolean(
      error &&
      typeof error === 'object' &&
      'stage' in error &&
      (error as { stage?: string }).stage === 'images-service' &&
      'kind' in error &&
      ((error as { kind?: CompleteServiceErrorKind }).kind === 'busy' ||
        (error as { kind?: CompleteServiceErrorKind }).kind === 'network')
    );

  const isClientNetworkError = (error: unknown) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;

    const status = getHttpStatus(error);
    if (status !== undefined) return false;

    const code = getAxiosCode(error);
    const message = getErrorMessage(error, '');

    return (
      code === 'ERR_NETWORK' ||
      code === 'ERR_INTERNET_DISCONNECTED' ||
      isNetworkLikeMessage(message)
    );
  };

  const classifyServiceError = (error: unknown): CompleteServiceErrorKind | null => {
    if (isCompleteServiceError(error) || isImageServiceError(error)) return error.kind;

    const message = getErrorMessage(error, '');
    const status = getHttpStatus(error);
    const code = getAxiosCode(error);

    // 超时：请求已发出但未及时收到响应 → 服务器繁忙
    if (
      code === 'ECONNABORTED' ||
      code === 'ETIMEDOUT' ||
      status === 408 ||
      status === 504 ||
      isTimeoutLikeMessage(message)
    ) {
      return 'busy';
    }

    // 有 HTTP 响应（含 4xx/5xx）→ 调用返回失败 → 服务器繁忙
    if (status !== undefined) {
      return 'busy';
    }

    // 无网络 / 请求未发出 / 未得到服务器响应 → 网络不稳定
    if (isClientNetworkError(error)) {
      return 'network';
    }

    return null;
  };

  const completeServiceErrorContent = computed(() => {
    const isImages = serviceErrorSource.value === 'images';

    if (completeServiceErrorKind.value === 'network') {
      return {
        title: '当前网络不稳定',
        message: isImages
          ? '绘本画面生成失败，请检查网络连接后重新尝试'
          : '请检查网络连接后重新尝试'
      };
    }

    return {
      title: '当前服务器繁忙',
      message: isImages
        ? '绘本画面生成失败，请重新尝试或稍后再试'
        : '请重新尝试或故事列表中稍后再试'
    };
  });

  const generationModalContent = computed(() => {
    if (generationPhase.value === 'complete') {
      if (isCompleteQueued.value) {
        return {
          title: '即将进入构思故事分镜....',
          subtitle: '正在排队中，请耐心等待~',
          image: completeStoryGif,
          alt: 'AI构思故事分镜',
          tipText: '',
          tipAction: ''
        };
      }
      return {
        title: 'AI正在构思每页故事分镜',
        subtitle: '构思过程不可中断，约1-2分钟，请耐心等待~',
        image: completeStoryGif,
        alt: 'AI构思故事分镜',
        tipText: '',
        tipAction: ''
      };
    }

    if (generationPhase.value === 'images') {
      if (isImagesQueued.value) {
        return {
          title: '即将进入绘制画面...',
          subtitle: '绘图排队中，请耐心等待',
          image: generateImagesGif,
          alt: 'AI绘制绘本画面',
          tipText: '关闭弹窗不影响生成，生成进度可在',
          tipAction: '我的故事列表'
        };
      }
      return {
        title: 'AI正在绘制绘本画面',
        subtitle: '预计还需 3–5 分钟，精彩作品值得耐心等待',
        image: generateImagesGif,
        alt: 'AI绘制绘本画面',
        tipText: '关闭弹窗不影响生成，生成进度可在',
        tipAction: '我的故事列表'
      };
    }

    if (generationPhase.value === 'voice') {
      return {
        title: '正在配音与翻页效果',
        subtitle: '正在为绘本添加声音与阅读动效',
        image: generateImagesGif,
        alt: '配音与翻页效果',
        tipText: '关闭弹窗不影响生成，生成进度可在',
        tipAction: '我的故事列表'
      };
    }

    if (generationPhase.value === 'organizing') {
      if (isOrganizingQueued.value) {
        return {
          title: '即将整理绘本...',
          subtitle: '排队中，请耐心等待',
          image: generateImagesGif,
          alt: '整理绘本内容',
          tipText: '关闭弹窗不影响生成，生成进度可在',
          tipAction: '我的故事列表'
        };
      }
      return {
        title: '正在整理绘本，即将完成',
        subtitle: '',
        image: generateImagesGif,
        alt: '整理绘本内容',
        tipText: '关闭弹窗不影响生成，生成进度可在',
        tipAction: '我的故事列表'
      };
    }

    return {
      title: '正在整理绘本，即将完成',
      subtitle: '',
      image: generateImagesGif,
      alt: '整理绘本内容',
      tipText: '关闭弹窗不影响生成，生成进度可在',
      tipAction: '我的故事列表'
    };
  });

  /** 生成中 GIF 自带浅色底，弹窗用左右微渐变贴合，避免露白边 */
  const generationModalBgClass = computed(() => {
    if (generationPhase.value === 'complete') {
      return 'generation-modal--complete-gif';
    }
    if (
      generationPhase.value === 'images' ||
      generationPhase.value === 'voice' ||
      generationPhase.value === 'organizing'
    ) {
      return 'generation-modal--images-gif';
    }
    return '';
  });

  const promptTags = [
    '恐龙大冒险',
    '不要在大树下躲雨',
    '失败一点也不可怕',
    '第一次上幼儿园',
    '和小熊分享蜂蜜',
    '帮妈妈照顾小花园'
  ];

  const scriptMock: ScriptPage[] = [
    '在一个阳光灿烂的早晨，小明醒来啦',
    '小明是一个充满好奇心的小朋友。',
    '今天，小明决定开始一场特别的冒险。',
    '路上遇到了许多新朋友，大家都很开心',
    '小明学会了关于「睡前」的道理。',
    '虽然遇到了一点小困难，但小明没有放弃。',
    '在朋友们的帮助下，小明成功啦！',
    '太阳落山了，小明高高兴兴地回家。',
    '太阳落山了，小明高高兴兴地回家。'
  ].map((text, index) => ({
    no: String(index + 1).padStart(2, '0'),
    page: index + 1,
    text,
    originalText: text
  }));

  const scriptPages = ref<ScriptPage[]>(scriptMock);
  const scriptListRef = ref<HTMLElement | null>(null);

  const autoResizeScriptEdit = (event: Event) => {
    const el = event.target as HTMLTextAreaElement | null;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const resizeAllScriptEdits = async () => {
    await nextTick();
    scriptListRef.value
      ?.querySelectorAll<HTMLTextAreaElement>('textarea.script-edit')
      .forEach((el) => {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
  };

  const isOutlineGenerating = computed(() => generating.value && !scriptPages.value.length);
  /** 大纲成功后展示：正文页数 + 封面；接口未返回前不显示 */
  const displayScriptPageCount = computed(() => {
    const pageCount = storyOutline.value?.pages?.length ?? scriptPages.value.length;
    if (!pageCount) return null;
    return pageCount + 1;
  });
  const characterCards = ref<CharacterCard[]>([
    {
      id: 'lele',
      name: '乐乐',
      imageUrl: '',
      description:
        '看起来约5岁，黑色短发，头顶扎两个小小的丸子头，系着红色发绳，大圆眼，棕色，眼神明亮，粉色棉布盘扣小袄，深蓝色棉布长裤，黑色棉布鞋'
    }
  ]);

  const pricingCharacterCount = computed(() => {
    if (storyOutline.value?.characters) {
      return countBillableCharacters(storyOutline.value.characters);
    }
    // 尚无大纲时，已有形象的自定义角色卡片不计费
    return characterCards.value.filter((card) => !card.imageUrl).length;
  });
  const pricingPageCount = computed(
    () => storyOutline.value?.pages?.length ?? scriptPages.value.length
  );
  const imagePriceLevel = computed(() =>
    normalizeImageGenerationPriceLevel(userStore.userData.uType)
  );

  const characterUnitPrice = computed(
    () => IMAGE_GENERATION_PRICE_BY_LEVEL[imagePriceLevel.value].character
  );
  const pageUnitPrice = computed(() => IMAGE_GENERATION_PRICE_BY_LEVEL[imagePriceLevel.value].page);

  const premiumTotalPrice = computed(
    () =>
      pricingCharacterCount.value * IMAGE_GENERATION_PRICE_BY_LEVEL[2].character +
      pricingPageCount.value * IMAGE_GENERATION_PRICE_BY_LEVEL[2].page
  );

  const estimatedTotalPrice = ref(0);
  /** 仅免费版、基础会员显示升级引导；高级会员不展示 */
  const showMemberDiscount = computed(() => imagePriceLevel.value < 2);
  const showCharacterMembershipUpgrade = computed(
    () => userStore.isLogin && (imagePriceLevel.value === 0 || imagePriceLevel.value === 1)
  );
  const membershipLevel = computed(() =>
    normalizeStoryMembershipLevel(
      userStore.userData.aipicturebook_vip_type ?? userStore.userData.uType ?? 0
    )
  );
  const memberDiscountSavings = computed(() =>
    Math.max(0, estimatedTotalPrice.value - premiumTotalPrice.value)
  );

  const optionFields: Array<{
    key: OptionKey;
    label: string;
    sheetTitle: string;
    options: Array<{
      value: string;
      label: string;
      /** 页数会员门槛：basic 基础会员，premium 高级会员；免费项不传 */
      membership?: 'basic' | 'premium';
      language?: string;
      language_code?: string;
    }>;
  }> = [
    {
      key: 'readerAge',
      label: '读者年龄',
      sheetTitle: '年龄段选择',
      options: [
        { value: '0-3岁', label: '0-3岁' },
        { value: '4-6岁', label: '4-6岁' },
        { value: '7-9岁', label: '7-9岁' },
        { value: '10-12岁', label: '10-12岁' },
        { value: '12岁以上', label: '12岁以上' }
      ]
    },
    {
      key: 'pageCount',
      label: '绘本页数',
      sheetTitle: '绘本页数选择',
      options: [
        { value: '5-10', label: '5-10' },
        { value: '10-15', label: '10-15', membership: 'basic' },
        { value: '15-20', label: '15-20', membership: 'basic' },
        { value: '20-25', label: '20-25', membership: 'premium' },
        { value: '25-30', label: '25-30', membership: 'premium' },
        { value: '30-35', label: '30-35', membership: 'premium' }
      ]
    },
    {
      key: 'artStyle',
      label: '绘本风格',
      sheetTitle: '绘本风格',
      options: [
        { value: '国风动画', label: '国风动画' },
        { value: '水彩童话', label: '水彩童话' },
        { value: '蜡笔涂鸦', label: '蜡笔涂鸦' },
        { value: '卡通简约', label: '卡通简约' },
        { value: '拼贴纸艺', label: '拼贴纸艺' },
        { value: '水墨国风', label: '水墨国风' },
        { value: '国风3D', label: '国风3D' },
        { value: '森林童话', label: '森林童话' },
        { value: '扁平几何', label: '扁平几何' },
        { value: '粉彩柔雾', label: '粉彩柔雾' },
        { value: '日式手绘', label: '日式手绘' },
        { value: 'Q版3D', label: 'Q版3D' },
        { value: '油画厚涂', label: '油画厚涂' },
        { value: '黏土定格', label: '黏土定格' }
      ]
    },
    {
      key: 'language',
      label: '绘本语言',
      sheetTitle: '绘本语言选择',
      options: [
        { value: 'zh', language_code: 'zh', label: '中文', language: '中文' },
        { value: 'en', language_code: 'en', label: '英文', language: '英文' },
        { value: 'zh_pinyin', language_code: 'zh_pinyin', label: '中文(拼音)', language: '中文' }
      ]
    },
    {
      key: 'narrator',
      label: '故事人称',
      sheetTitle: '故事人称选择',
      options: [
        { value: 'first', label: '第一人称' },
        { value: 'third', label: '第三人称' }
      ]
    }
  ];

  const parsePageRange = (value: string) => {
    const numbers = value.match(/\d+/g)?.map(Number) ?? [];
    return {
      min: numbers[0] ?? 5,
      max: numbers[1] ?? numbers[0] ?? 10
    };
  };

  const currentLanguageOption = () =>
    optionFields
      .find((field) => field.key === 'language')
      ?.options.find((option) => option.value === form.value.language);

  const currentPerspectiveLabel = () => (form.value.narrator === 'first' ? '第一人称' : '第三人称');

  const buildScriptPages = (targetStory: StoryBook): ScriptPage[] =>
    [...(targetStory.pages ?? [])]
      .sort((a, b) => a.page - b.page)
      .map((page) => ({
        no: String(page.page).padStart(2, '0'),
        page: page.page,
        text: page.narration ?? '',
        originalText: page.narration ?? ''
      }));

  const buildOutlineScriptPages = (outline: StoryOutline): ScriptPage[] =>
    [...(outline.pages ?? [])]
      .sort((a, b) => a.page - b.page)
      .map((page) => ({
        no: String(page.page).padStart(2, '0'),
        page: page.page,
        text: page.narration ?? '',
        originalText: page.narration ?? ''
      }));

  const truncateText = (value: string, maxLength = 72) => {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength)}...`;
  };

  /** 接口常把各外观字段都填成「以用户角色参考图为准」，拼接后会重复，展示时只保留一句 */
  const USER_REF_APPEARANCE_HINT = '以用户角色参考图为准';
  const collapseRepeatedUserRefHint = (value: string) => {
    const text = value.trim();
    if (!text.includes(USER_REF_APPEARANCE_HINT)) return text;
    const stripNoise = (input: string) => input.replace(/[，,。.\s]/g, '');
    const compact = stripNoise(text);
    const hint = stripNoise(USER_REF_APPEARANCE_HINT);
    if (compact && compact.replaceAll(hint, '') === '') {
      return USER_REF_APPEARANCE_HINT;
    }
    return text;
  };

  const buildAppearanceDescription = (appearance?: StoryAppearance) => {
    if (!appearance) return '';
    const parts = [
      appearance.age_look,
      appearance.hair,
      appearance.eyes,
      appearance.skin,
      appearance.build,
      appearance.outfit,
      appearance.distinctive_feature
    ]
      .map((part) => part?.trim())
      .filter((part): part is string => Boolean(part));
    const uniqueParts = [...new Set(parts)];
    return collapseRepeatedUserRefHint(uniqueParts.join('，'));
  };

  const buildCharacterDescription = (character: StoryCharacter) => {
    if (character.reference_image_url || character.input_reference_image_url) {
      return truncateText(
        collapseRepeatedUserRefHint(character.personality || USER_REF_APPEARANCE_HINT),
        24
      );
    }

    const description = buildAppearanceDescription(character.appearance);
    return truncateText(
      collapseRepeatedUserRefHint(description || character.personality || '暂无角色描述')
    );
  };

  const buildFormCharacterCards = (character: StoryCharacter): CharacterCard[] =>
    (character.forms ?? []).map((formItem) => {
      const hasRefImage = Boolean(
        formItem.reference_image_url || formItem.input_reference_image_url
      );
      const rawDescription =
        buildAppearanceDescription(formItem.appearance) ||
        formItem.description ||
        formItem.consistency_prompt ||
        formItem.visual_keywords?.join('，') ||
        (hasRefImage ? USER_REF_APPEARANCE_HINT : '暂无形态描述');

      return {
        id: `${character.id}-${formItem.id}`,
        name: `${character.name}-${formItem.name}`,
        imageUrl: formItem.reference_image_url || formItem.input_reference_image_url || '',
        description: truncateText(
          collapseRepeatedUserRefHint(rawDescription),
          hasRefImage ? 24 : 72
        )
      };
    });

  const buildCharacterCards = (characters: StoryCharacter[]): CharacterCard[] =>
    characters.flatMap((character) => {
      if (character.forms?.length) return buildFormCharacterCards(character);
      return [
        {
          id: character.id,
          name: character.name,
          imageUrl: character.reference_image_url || character.input_reference_image_url || '',
          description: buildCharacterDescription(character)
        }
      ];
    });

  /** 用户自定义（@引用）等已有参考图的角色无需再生图，不计角色形象费用 */
  const characterNeedsImageGeneration = (character: StoryCharacter) => {
    const forms = character.forms ?? [];
    if (forms.length) {
      return forms.some(
        (formItem) => !formItem.input_reference_image_url && !formItem.reference_image_url
      );
    }
    return !character.input_reference_image_url && !character.reference_image_url;
  };

  const countBillableCharacters = (characters: StoryCharacter[] | undefined | null) =>
    (characters ?? []).filter(characterNeedsImageGeneration).length;

  const syncCharacterCards = (targetStory: StoryBook | null) => {
    const characters = targetStory?.characters ?? [];
    if (!characters.length) return;

    characterCards.value = buildCharacterCards(characters);
  };

  const updateStory = (nextStory: StoryBook, projectId?: string | number) => {
    story.value = nextStory;
    if (projectId) storyProjectId.value = projectId;
    scriptPages.value = buildScriptPages(nextStory);
    syncCharacterCards(nextStory);
  };

  const buildLayoutPayload = () => {
    const layout = currentLayoutOption.value;

    return {
      layout: layout.value,
      aspect_ratio: layout.page_aspect_ratio,
      page_aspect_ratio: layout.page_aspect_ratio,
      character_aspect_ratio: layout.character_aspect_ratio,
      cover_aspect_ratio: layout.cover_aspect_ratio
    };
  };

  const syncStoryRequestLayout = () => {
    if (!storyRequest.value) return;
    storyRequest.value = {
      ...storyRequest.value,
      ...buildLayoutPayload()
    };
  };

  const getKnownCharacterNames = () =>
    [
      ...new Set([
        ...mentionCharacters.value.map((item) => item.name).filter(Boolean),
        ...trackedMentionNames.value
      ])
    ].sort((a, b) => b.length - a.length);

  const extractMentionedCharacterNames = (description: string) => {
    const names = getKnownCharacterNames();
    const mentioned: string[] = [];

    for (let i = 0; i < description.length; i += 1) {
      if (description[i] !== '@') continue;

      const rest = description.slice(i + 1);
      const matched = names.find((name) => rest.startsWith(name));
      if (!matched) continue;

      mentioned.push(matched);
      i += matched.length;
    }

    return mentioned;
  };

  const getUniqueMentionedCharacterNames = (description = form.value.description) => {
    const fromEditor = promptEditorRef.value?.getMentionedNames?.() ?? [];
    const fromText = extractMentionedCharacterNames(description);
    return [...new Set([...fromEditor, ...fromText])];
  };

  const syncTrackedMentionNames = () => {
    trackedMentionNames.value = getUniqueMentionedCharacterNames();
  };

  /** 未达上限，或再次引用已存在角色时允许 */
  const canMentionMoreCharacters = (characterName?: string) => {
    syncTrackedMentionNames();
    const mentioned = trackedMentionNames.value;
    if (characterName && mentioned.includes(characterName)) return true;
    return mentioned.length < MAX_MENTION_CHARACTERS;
  };

  const warnMentionLimit = () => {
    ElMessage.warning(`最多只能添加${MAX_MENTION_CHARACTERS}个角色`);
  };

  const formatThemeForApi = (description: string) => {
    let theme = description.trim();
    getKnownCharacterNames().forEach((name) => {
      theme = theme.split(`@${name}`).join(name);
    });
    return theme;
  };

  const buildCharacterRefs = (description: string): InputCharacterRef[] => {
    const characterByName = new Map(
      mentionCharacters.value.filter((item) => item.name).map((item) => [item.name, item])
    );

    return [...new Set(extractMentionedCharacterNames(description))]
      .map((name) => {
        const character = characterByName.get(name);
        if (!character) return null;

        return {
          name,
          setting: character.setting || character.description || '',
          image_url: character.generatedUrl || character.refImageUrl || ''
        };
      })
      .filter((item): item is InputCharacterRef => item !== null);
  };

  const buildStoryPayload = (): StoryCreatePayload => {
    const description = form.value.description.trim();
    const characterRefs = buildCharacterRefs(description);
    const languageOption = currentLanguageOption();

    return {
      theme: formatThemeForApi(description),
      language: languageOption?.language ?? '中文',
      language_code: languageOption?.language_code ?? form.value.language,
      art_style: form.value.artStyle,
      target_age: form.value.readerAge,
      perspective: currentPerspectiveLabel(),
      page_count: parsePageRange(form.value.pageCount),
      extra_requirement: '每页旁白简短，画面适合生成绘本插图。',
      ...(characterRefs.length ? { character_refs: characterRefs } : {}),
      ...buildLayoutPayload()
    };
  };

  const buildOutlinePayload = (payload: StoryCreatePayload): StoryOutlineCreatePayload => {
    const outlinePayload = { ...payload };
    delete outlinePayload.layout;
    delete outlinePayload.aspect_ratio;
    delete outlinePayload.page_aspect_ratio;
    delete outlinePayload.character_aspect_ratio;
    delete outlinePayload.cover_aspect_ratio;

    return outlinePayload;
  };

  const syncOutlineCharacterCards = (outline: StoryOutline | null) => {
    const characters = outline?.characters ?? [];
    if (!characters.length) return;

    characterCards.value = buildCharacterCards(characters);
  };

  const updateOutline = (outline: StoryOutline) => {
    storyOutline.value = outline;
    outlineTitle.value = outline.title ?? '';
    scriptPages.value = buildOutlineScriptPages(outline);
    syncOutlineCharacterCards(outline);
    story.value = null;
    void resizeAllScriptEdits();
  };

  const syncOutlineNarration = () => {
    if (!storyOutline.value) return null;

    const pages = scriptPages.value.map((page) => ({
      ...(storyOutline.value?.pages.find((item) => item.page === page.page) ?? { page: page.page }),
      narration: page.text.trim()
    }));

    storyOutline.value = {
      ...storyOutline.value,
      title: outlineTitle.value.trim() || storyOutline.value.title,
      pages
    };
    story.value = story.value ? { ...story.value, title: storyOutline.value.title } : null;
    return storyOutline.value;
  };

  const syncMentionPopupLayout = () => {
    if (isMobileViewportActive.value) {
      mentionPopupStyle.value = {};
      return;
    }

    if (!mentionWrapRef.value) return;

    const rect = mentionWrapRef.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = MENTION_POPUP_GAP;
    const margin = MENTION_POPUP_MARGIN;
    const spaceRight = viewportWidth - rect.right - gap - margin;
    const maxWidth = Math.min(
      MENTION_POPUP_WIDTH,
      Math.max(0, spaceRight),
      viewportWidth - margin * 2
    );
    const maxHeight = Math.min(
      MENTION_POPUP_HEIGHT,
      viewportHeight - margin * 2,
      Math.max(0, viewportHeight - rect.top - margin)
    );
    const scale = Math.min(maxWidth / MENTION_POPUP_WIDTH, maxHeight / MENTION_POPUP_HEIGHT);

    mentionPopupStyle.value = {
      position: 'fixed',
      left: `${rect.right + gap}px`,
      top: `${Math.max(margin, rect.top - 10)}px`,
      width: `${maxWidth}px`,
      height: `${maxHeight}px`,
      '--mention-scale': String(scale)
    };
  };

  const handleMentionPopupResize = () => {
    isMobileViewportActive.value = isMobileViewport();
    if (mentionModalVisible.value) syncMentionPopupLayout();
  };

  const mergePinnedMentionCharacters = (characters: CharacterItem[]) => {
    const merged = [...characters];
    pinnedMentionCharacters.value.forEach((character) => {
      const existingIndex = merged.findIndex((item) => item.id === character.id);
      if (existingIndex >= 0) merged.splice(existingIndex, 1);
      merged.push(character);
    });
    return merged;
  };

  const pinMentionCharacter = (character: CharacterItem) => {
    pinnedMentionCharacters.value = [
      ...pinnedMentionCharacters.value.filter((item) => item.id !== character.id),
      character
    ];
    mentionCharacters.value = mergePinnedMentionCharacters(mentionCharacters.value);
  };

  const loadMentionCharacters = async () => {
    if (mentionCharactersRequest) return mentionCharactersRequest;

    const request = (async () => {
      mentionLoading.value = true;
      try {
        const result = await fetchCharacterList();
        const availableCharacters = result.list.filter(
          (character) => character.name.trim() && character.setting.trim()
        );
        mentionCharacters.value = mergePinnedMentionCharacters(availableCharacters);
        mentionCharactersLoaded = true;
      } catch {
        mentionCharacters.value = mergePinnedMentionCharacters([]);
        mentionCharactersLoaded = false;
      } finally {
        mentionLoading.value = false;
      }
    })();

    mentionCharactersRequest = request;
    try {
      await request;
    } finally {
      if (mentionCharactersRequest === request) mentionCharactersRequest = null;
    }
  };

  const getRouteCharacterId = () => {
    const value = route.query.characterId;
    const characterId = Array.isArray(value) ? value[0] : value;
    return typeof characterId === 'string' ? characterId : '';
  };

  const applyRouteCharacter = async () => {
    const characterId = getRouteCharacterId();
    if (!characterId) return;

    const requestId = routeCharacterRequestId + 1;
    routeCharacterRequestId = requestId;
    const isCurrentRequest = () =>
      requestId === routeCharacterRequestId &&
      route.name === 'create' &&
      getRouteCharacterId() === characterId;

    if (!mentionCharactersLoaded) await loadMentionCharacters();
    if (!isCurrentRequest()) return;

    let character = mentionCharacters.value.find((item) => item.id === characterId);
    if (!character) {
      try {
        character = await getCharacter(characterId);
      } catch {
        if (isCurrentRequest()) {
          ElMessage.error('角色信息加载失败，请稍后重试');
        }
        return;
      }
    }

    if (!isCurrentRequest()) return;

    if (!character.name.trim()) {
      ElMessage.error('角色信息不完整，暂时无法用于创作');
      return;
    }

    pinMentionCharacter(character);
    if (extractMentionedCharacterNames(form.value.description).includes(character.name)) return;
    if (!canMentionMoreCharacters(character.name)) {
      warnMentionLimit();
      return;
    }

    await nextTick();
    if (!isCurrentRequest()) return;
    await promptEditorRef.value?.appendMention(character);
    syncTrackedMentionNames();
  };

  const resetCharacterForm = () => {
    pendingCreatedCharacterId.value = '';
    characterGenerationRequestId.value += 1;
    characterGenerationStep.value = 'form';
    characterGenerating.value = false;
    characterSaving.value = false;
    Object.assign(characterForm, emptyCharacterForm());
    delete characterForm.id;
  };

  const handleCharacterEditorClosed = async () => {
    const shouldShowMentionPopup = showMentionPopupAfterCharacterClose.value;
    showMentionPopupAfterCharacterClose.value = false;
    resetCharacterForm();
    await loadMentionCharacters();
    if (shouldShowMentionPopup) await openMentionPopup();
  };

  const openCreateCharacterDialog = () => {
    resetCharacterForm();
    // 与角色管理页一致：桌面端隐藏名称输入，需预填默认名才能启用「AI 生成角色」
    characterForm.name = '乐乐';
    characterAppearanceTab.value = 'image';
    characterGenerationStep.value = 'form';
    characterDialogVisible.value = true;
  };

  const generateCurrentCharacter = async () => {
    if (!canGenerateCharacter.value) return false;

    const requestId = characterGenerationRequestId.value + 1;
    characterGenerationRequestId.value = requestId;
    const requestedName = characterForm.name;
    const requestedSetting = characterForm.setting;
    characterGenerationStep.value = 'preview';
    characterForm.generatedUrl = '';
    characterForm.avatarUrl = '';
    characterGenerating.value = true;

    try {
      const created = await createCharacter(
        {
          ...characterForm,
          refImageUrl: characterAppearanceTab.value === 'image' ? characterForm.refImageUrl : ''
        },
        pendingCreatedCharacterId.value || undefined
      );
      // 角色生成成功后异步刷新余额，余额查询失败不应阻塞当前角色生成流程。
      void balanceStore.refreshBalance();

      if (characterGenerationRequestId.value !== requestId) {
        void loadMentionCharacters();
        return false;
      }

      const currentName = characterForm.name;
      const currentSetting = characterForm.setting;
      Object.assign(characterForm, created);
      if (currentName !== requestedName) characterForm.name = currentName;
      if (currentSetting !== requestedSetting) characterForm.setting = currentSetting;
      pendingCreatedCharacterId.value = created.id;

      if (!created.taskId) throw new Error('角色生成任务缺少任务ID');
      const completed = resolveCharacterTaskResult(
        await waitForCharacterTask(created.taskId),
        created
      );
      if (characterGenerationRequestId.value !== requestId) return false;

      const latestName = characterForm.name;
      const latestSetting = characterForm.setting;
      Object.assign(characterForm, completed);
      characterForm.name = latestName;
      characterForm.setting = latestSetting;
      return true;
    } catch (error) {
      if (characterGenerationRequestId.value === requestId) {
        if (isTextScanFailedError(error)) {
          showTextScanFailedError();
        } else {
          ElMessage.error(getErrorMessage(error, '角色生成失败，请稍后重试'));
        }
        characterGenerationStep.value = 'form';
      }
      return false;
    } finally {
      if (characterGenerationRequestId.value === requestId) characterGenerating.value = false;
    }
  };

  const handleCharacterSave = () => {
    if (!canGenerateCharacter.value || characterGenerating.value || characterSaving.value) return;
    void generateCurrentCharacter();
  };

  const handleRedescribeCharacter = () => {
    if (characterGenerating.value || characterSaving.value) return;
    // “重新描述角色”只返回表单供用户修改，确认描述后才会再次发起生成请求。
    characterGenerationStep.value = 'form';
  };

  const handleConfirmCharacterGeneration = async () => {
    if (!canConfirmCharacterGeneration.value || !characterForm.id) return;

    characterSaving.value = true;
    try {
      await updateCharacter(characterForm.id, {
        name: characterForm.name,
        setting: characterForm.setting
      });
      pendingCreatedCharacterId.value = '';
      showMentionPopupAfterCharacterClose.value = true;
      characterDialogVisible.value = false;
      ElMessage.success('角色已创建');
    } catch (error) {
      if (isTextScanFailedError(error)) {
        showTextScanFailedError();
      } else {
        ElMessage.error(getErrorMessage(error, '角色保存失败，请稍后重试'));
      }
    } finally {
      characterSaving.value = false;
    }
  };

  const savePromptCaret = () => {
    promptEditorRef.value?.lockCaretFromSelection();
  };

  const openMentionPopup = async () => {
    isMobileViewportActive.value = isMobileViewport();
    hasOpenedMentionPopup.value = true;
    mentionModalVisible.value = true;
    selectedMentionId.value = '';
    if (isMobileViewportActive.value) {
      promptEditorRef.value?.blurEditor();
    }
    await nextTick();
    syncMentionPopupLayout();
    await loadMentionCharacters();
    // 角色列表更新可能触发编辑器重绘；移动端再 blur 一次，避免键盘被重新拉起
    if (isMobileViewportActive.value) {
      promptEditorRef.value?.blurEditor();
    }
  };

  /** 输入框内键入 @：与点击「@ 选择角色」相同，打开角色选择弹层 */
  const handleMentionTrigger = () => {
    if (mentionModalVisible.value) return;
    void openMentionPopup();
  };

  const closeMentionPopup = () => {
    mentionModalVisible.value = false;
    selectedMentionId.value = '';
    promptEditorRef.value?.unlockCaret();
  };

  const toggleMentionPopup = () => {
    if (mentionModalVisible.value) {
      closeMentionPopup();
      return;
    }
    void openMentionPopup();
  };

  const selectMentionCharacter = async (character: CharacterItem) => {
    if (!canMentionMoreCharacters(character.name)) {
      warnMentionLimit();
      return;
    }
    selectedMentionId.value = character.id;
    await promptEditorRef.value?.appendMention(character);
    syncTrackedMentionNames();
    closeMentionPopup();
  };

  const goCreateCharacter = () => {
    closeMentionPopup();
    openCreateCharacterDialog();
  };

  const handleMentionOutsideClick = (event: MouseEvent) => {
    if (!mentionModalVisible.value || isMobileViewportActive.value) return;
    const target = event.target as Node;
    if (mentionWrapRef.value && !mentionWrapRef.value.contains(target)) {
      closeMentionPopup();
    }
  };

  const handleScriptEditResize = () => {
    handleMentionPopupResize();
    if (currentStep.value === 'script') void resizeAllScriptEdits();
  };

  onMounted(() => {
    isMobileViewportActive.value = isMobileViewport();
    document.addEventListener('click', handleMentionOutsideClick);
    window.addEventListener('resize', handleScriptEditResize);
    void loadMentionCharacters();
    if (currentStep.value === 'script') void resizeAllScriptEdits();
  });

  onActivated(() => {
    isCreatePageActive.value = true;
    void applyRouteCharacter();
    void showOnboardingWhenReady();
  });

  onDeactivated(() => {
    isCreatePageActive.value = false;
    insufficientCoinsModalVisible.value = false;
    closeMentionPopup();
  });

  watch(
    () => route.query.characterId,
    () => {
      if (route.name === 'create') void applyRouteCharacter();
    }
  );

  watch(
    [
      () => userStore.isLogin,
      () => userStore.userMeta.hasOnboarding,
      dashboardDialogsReady,
      coinGiftDialogOpen,
      () => route.name
    ],
    () => void showOnboardingWhenReady(),
    { immediate: true }
  );

  watch(
    () => form.value.description,
    () => {
      syncTrackedMentionNames();
    }
  );

  watch(
    () => [currentStep.value, outlineTitle.value, scriptPages.value] as const,
    ([step]) => {
      if (step === 'script') void resizeAllScriptEdits();
    },
    { deep: true }
  );

  onBeforeUnmount(() => {
    stopOutlineFakeProgress();
    document.removeEventListener('click', handleMentionOutsideClick);
    window.removeEventListener('resize', handleScriptEditResize);
  });

  const usePrompt = (tag: string) => {
    form.value.description = tag;
  };

  const openLayoutModal = () => {
    isMobileViewportActive.value = isMobileViewport();
    pendingLayout.value = selectedLayout.value;
    layoutModalVisible.value = true;
  };

  const closeLayoutModal = () => {
    layoutModalVisible.value = false;
  };

  const confirmLayout = () => {
    const option = layoutOptions.find((item) => item.value === pendingLayout.value);
    if (!hasMembershipAccess(userStore.userData, option?.membership)) {
      showUpgrade();
      return;
    }

    selectedLayout.value = pendingLayout.value;
    syncStoryRequestLayout();
    closeLayoutModal();
  };

  const selectLayoutOption = (value: LayoutValue) => {
    const option = layoutOptions.find((item) => item.value === value);
    if (!hasMembershipAccess(userStore.userData, option?.membership)) {
      showUpgrade();
      return;
    }

    pendingLayout.value = value;
    if (isMobileViewportActive.value) {
      confirmLayout();
    }
  };

  const openArtStyleModal = () => {
    isMobileViewportActive.value = isMobileViewport();
    pendingImageModel.value = selectedImageModel.value;
    pendingArtStyle.value = form.value.artStyle;
    artStyleModalVisible.value = true;
  };

  const closeArtStyleModal = () => {
    artStyleModalVisible.value = false;
  };

  // const selectImageModelTab = (model: ImageModelKind) => {
  //   if (pendingImageModel.value === model) return;
  //   pendingImageModel.value = model;
  //   const options = artStylesByModel[model];
  //   if (!options.some((item) => item.value === pendingArtStyle.value)) {
  //     pendingArtStyle.value = options[0]?.value ?? pendingArtStyle.value;
  //   }
  // };

  const confirmArtStyle = () => {
    const option = pendingArtStyleOptions.value.find(
      (item) => item.value === pendingArtStyle.value
    );
    if (!hasMembershipAccess(userStore.userData, option?.membership)) {
      showUpgrade();
      return;
    }

    selectedImageModel.value = pendingImageModel.value;
    form.value.artStyle = pendingArtStyle.value;
    closeArtStyleModal();
  };

  const selectArtStyleOption = (value: string) => {
    const option = pendingArtStyleOptions.value.find((item) => item.value === value);
    if (!hasMembershipAccess(userStore.userData, option?.membership)) {
      showUpgrade();
      return;
    }

    pendingArtStyle.value = value;
    if (isMobileViewportActive.value) {
      confirmArtStyle();
    }
  };

  const openVoiceModal = () => {
    voiceModalVisible.value = true;
  };

  const confirmVoice = (result: VoiceSelectResult) => {
    selectedVoice.value = result.voice;
    voiceVolume.value = result.volume;
    voiceSpeed.value = result.speed;
  };

  const openMusicModal = () => {
    musicModalVisible.value = true;
  };

  const confirmMusic = (result: MusicSelectResult) => {
    musicVolume.value = result.volume;
    selectedMusicId.value = result.id;

    if (result.id === null) {
      selectedMusicName.value = '';
      selectedMusicUrl.value = null;
      musicModalVisible.value = false;
      return;
    }

    if (result.id === '__upload__' && result.contentUrl) {
      selectedMusicName.value = result.trackName ?? '自定义上传';
      selectedMusicUrl.value = result.contentUrl;
      musicModalVisible.value = false;
      return;
    }

    if (result.track) {
      selectedMusicName.value = result.track.name;
      selectedMusicUrl.value = result.track.url ?? null;
    } else {
      selectedMusicName.value = result.trackName ?? '';
      selectedMusicUrl.value = null;
    }
    musicModalVisible.value = false;
  };

  const resetOutlineView = () => {
    stopOutlineFakeProgress();
    storyOutline.value = null;
    outlineTitle.value = '';
    outlineProgress.value = 0;
    isOutlineQueued.value = false;
    story.value = null;
    storyProjectId.value = undefined;
    pendingCompleteProjectId.value = undefined;
    workULink.value = undefined;
    workLinkName.value = undefined;
    scriptPages.value = [];
    characterCards.value = [];
    estimatedTotalPrice.value = 0;
  };

  /** 查询本次预计消耗云币；接口失败时回退本地会员单价估算，避免按钮显示空白 */
  const refreshEstimatedTotalPrice = async (outline?: StoryOutline | null) => {
    const target = outline ?? storyOutline.value;
    if (!target) {
      estimatedTotalPrice.value = 0;
      return;
    }

    const localFallback =
      countBillableCharacters(target.characters) * characterUnitPrice.value +
      (target.pages?.length ?? 0) * pageUnitPrice.value;

    try {
      const result = await previewStoryCost({ outline: target });
      estimatedTotalPrice.value = result.cost;
    } catch (error) {
      console.warn('[preview-cost] 查询费用失败，回退本地估算:', error);
      estimatedTotalPrice.value = localFallback;
    }
  };

  const requestStoryOutline = async (payload: StoryCreatePayload) => {
    isOutlineQueued.value = true;
    stopOutlineFakeProgress();
    outlineProgress.value = 0;
    try {
      const submitted = await createStoryOutline(buildOutlinePayload(payload));
      const task = await pollTask<StoryOutlineResult>(submitted.task_id, {
        intervalMs: 2000,
        onUpdate: (snapshot) => handleOutlineTaskUpdate(snapshot.status)
      });

      if (task.status === 'failed') {
        throw new Error(task.error || '故事大纲生成失败，请稍后重试');
      }
      // 大纲任务完成后同步本次生成产生的云币消耗。
      void balanceStore.refreshBalance();
      const outline = task.result?.outline;
      if (!outline) throw new Error('故事大纲生成结果为空');
      isOutlineQueued.value = false;
      stopOutlineFakeProgress({ complete: true });
      updateOutline(outline);
    } catch (error) {
      isOutlineQueued.value = false;
      stopOutlineFakeProgress();
      outlineProgress.value = 0;
      throw error;
    }
  };

  const handleGenerate = async () => {
    if (
      !form.value.description.trim() ||
      generating.value ||
      isCheckingStorage.value ||
      createRequestPending
    ) {
      return;
    }

    const phoneBindingGuard = phoneBindingGuardRef.value;
    if (!phoneBindingGuard) return;

    createRequestPending = true;
    try {
      if (!(await phoneBindingGuard.ensurePhoneBound())) return;
    } finally {
      createRequestPending = false;
    }

    const storyLimit = getStoryStorageLimit(membershipLevel.value);
    if (Number.isFinite(storyLimit)) {
      isCheckingStorage.value = true;
      try {
        const result = await fetchStoryList({ current: 1, size: 1 });
        if (result.total >= storyLimit) {
          isStorageLimitVisible.value = true;
          return;
        }
      } catch (error) {
        console.error('检查绘本存储额度失败', error);
        ElMessage.error(getErrorMessage(error, '检查绘本存储额度失败，请稍后重试'));
        return;
      } finally {
        isCheckingStorage.value = false;
      }
    }

    const payload = buildStoryPayload();
    storyRequest.value = payload;
    resetOutlineView();
    currentStep.value = 'script';
    generating.value = true;

    try {
      await requestStoryOutline(payload);
    } catch (error: any) {
      if (isTextScanFailedError(error)) {
        showTextScanFailedError();
      } else {
        ElMessage.error(error?.message || '故事大纲生成失败，请稍后重试');
      }
      currentStep.value = 'create';
    } finally {
      generating.value = false;
    }
  };

  const handleManageStories = () => {
    isStorageLimitVisible.value = false;
    void router.push({ name: 'story' });
  };

  const refreshScript = async () => {
    if (!storyRequest.value) {
      ElMessage.warning('请先生成故事大纲');
      return;
    }

    const payload = storyRequest.value;
    resetOutlineView();
    generating.value = true;

    try {
      await requestStoryOutline(payload);
    } catch (error: any) {
      if (isTextScanFailedError(error)) {
        showTextScanFailedError();
      } else {
        ElMessage.error(error?.message || '故事大纲生成失败，请稍后重试');
      }
    } finally {
      generating.value = false;
    }
  };

  const handleScriptNext = async () => {
    const outline = syncOutlineNarration();
    if (!outline || !storyRequest.value) {
      ElMessage.warning('请先生成故事大纲');
      return;
    }

    generatingStoryboard.value = true;
    try {
      const hasEdits = scriptPages.value.some(
        (page) => page.text.trim() && page.text.trim() !== page.originalText.trim()
      );

      if (hasEdits) {
        const submitted = await repairStoryOutline({
          request: storyRequest.value,
          outline,
          edit_instruction:
            '用户修改了页面旁白，请检查是否需要增删改角色，并同步 pages[].character_ids。'
        });
        const task = await pollTask<StoryOutlineRepairResult>(submitted.task_id, {
          intervalMs: 2000
        });

        if (task.status === 'failed') {
          throw new Error(task.error || '大纲修复失败，请稍后重试');
        }
        // 大纲修复是独立的生成任务，完成后需要再次同步余额。
        void balanceStore.refreshBalance();

        const repairedOutline = task.result?.outline;
        if (!repairedOutline) throw new Error('大纲修复结果为空');
        updateOutline(repairedOutline);
      } else {
        syncOutlineCharacterCards(outline);
      }

      await refreshEstimatedTotalPrice(storyOutline.value);
      currentStep.value = 'style';
      void initDefaultVoice();
    } catch (error: any) {
      if (isTextScanFailedError(error)) {
        showTextScanFailedError();
      } else {
        ElMessage.error(error?.message || '大纲修复失败，请稍后重试');
      }
    } finally {
      generatingStoryboard.value = false;
    }
  };

  const shouldGenerateCharacterPreviews = (targetStory: StoryBook) =>
    (targetStory.characters ?? []).some(characterNeedsImageGeneration);

  const calcCompleteStoryProgress = (elapsedMs: number) => {
    const fastStageMs = 100000;
    // 原算法峰值约 30，按比例映射到 story 阶段虚拟进度 0–40
    const scale = 40 / 30;
    if (elapsedMs <= fastStageMs) {
      return 25 * scale * Math.sin((elapsedMs / fastStageMs) * (Math.PI / 2));
    }

    return scale * (30 - 5 * Math.exp(-(elapsedMs - fastStageMs) / 120000));
  };

  const formatDuration = (startTime: number, endTime = performance.now()) =>
    `${((endTime - startTime) / 1000).toFixed(1)}s`;

  type VirtualProgressKind = 'story' | 'transfer';

  type VirtualProgressController = {
    startTime: number;
    timer: number | null;
    kind: VirtualProgressKind | null;
  };

  const startCompleteStoryProgress = (): VirtualProgressController => {
    const startTime = performance.now();
    generationProgress.value = 0;
    generationPhase.value = 'complete';

    const controller: VirtualProgressController = {
      startTime,
      timer: null,
      kind: 'story'
    };

    controller.timer = window.setInterval(() => {
      // 仅 story/draft（complete 阶段）使用虚拟进度 0–40
      if (generationPhase.value !== 'complete' || controller.kind !== 'story') return;
      generationProgress.value = Math.min(
        39,
        Math.floor(calcCompleteStoryProgress(performance.now() - controller.startTime))
      );
    }, 500);

    return controller;
  };

  const stopVirtualProgress = (controller?: VirtualProgressController | null) => {
    if (!controller?.timer) return;
    window.clearInterval(controller.timer);
    controller.timer = null;
    controller.kind = null;
  };

  /** transfer 阶段虚构进度 96→99，done 时再拉满 100 */
  const startTransferVirtualProgress = (
    controller?: VirtualProgressController | null
  ): VirtualProgressController => {
    const target = controller ?? { startTime: performance.now(), timer: null, kind: null };
    if (target.kind === 'transfer' && target.timer) return target;

    stopVirtualProgress(target);
    target.startTime = performance.now();
    target.kind = 'transfer';
    generationPhase.value = 'organizing';
    generationProgress.value = Math.max(96, generationProgress.value);

    target.timer = window.setInterval(() => {
      if (generationPhase.value !== 'organizing' || target.kind !== 'transfer') return;
      const elapsed = performance.now() - target.startTime;
      const ratio = 1 - Math.exp(-elapsed / 12000);
      generationProgress.value = Math.min(99, Math.floor(96 + 3 * ratio));
    }, 400);

    return target;
  };

  const clearGenerationQueuedFlags = () => {
    isCompleteQueued.value = false;
    isImagesQueued.value = false;
    isOrganizingQueued.value = false;
  };

  /**
   * 将 /get-progress 映射到弹窗：
   * - status=failed → 由轮询结束处展示重试
   * - status=queued → 对应 stage 的排队文案
   * - status=running → 按 stage 展示不同 UI / 进度
   * - stage=story：complete UI + 虚拟 0–40
   * - stage=draft：即将进入绘制画面 UI
   * - stage=image：images UI + 接口 progress
   * - stage=transfer：整理 UI + 虚拟 96–100
   * - stage=done：完成
   */
  const applyGenerationProgressFromStory = (
    snapshot: StoryProgressSnapshot,
    virtualProgress?: VirtualProgressController | null
  ) => {
    if (snapshot.status === 'failed' || snapshot.stage === 'failed') {
      stopVirtualProgress(virtualProgress);
      clearGenerationQueuedFlags();
      return;
    }

    const stage = snapshot.stage;
    const isQueued = snapshot.status === 'queued';

    switch (stage) {
      case 'story': {
        generationPhase.value = 'complete';
        isCompleteQueued.value = isQueued;
        isImagesQueued.value = false;
        isOrganizingQueued.value = false;
        // running：虚拟进度；queued：只切排队文案
        if (!isQueued && virtualProgress && virtualProgress.kind !== 'story') {
          stopVirtualProgress(virtualProgress);
          const restarted = startCompleteStoryProgress();
          virtualProgress.startTime = restarted.startTime;
          virtualProgress.timer = restarted.timer;
          virtualProgress.kind = restarted.kind;
        }
        break;
      }
      case 'draft': {
        // draft：显示「即将进入绘制画面...」文案（images 排队态）
        stopVirtualProgress(virtualProgress);
        generationPhase.value = 'images';
        isCompleteQueued.value = false;
        isImagesQueued.value = true;
        isOrganizingQueued.value = false;
        generationProgress.value = Math.max(40, generationProgress.value);
        break;
      }
      case 'image': {
        stopVirtualProgress(virtualProgress);
        generationPhase.value = 'images';
        isCompleteQueued.value = false;
        isImagesQueued.value = isQueued;
        isOrganizingQueued.value = false;
        if (
          !isQueued &&
          typeof snapshot.progress === 'number' &&
          Number.isFinite(snapshot.progress)
        ) {
          generationProgress.value = Math.max(0, Math.min(99, Math.floor(snapshot.progress)));
        }
        break;
      }
      case 'transfer': {
        generationPhase.value = 'organizing';
        isCompleteQueued.value = false;
        isImagesQueued.value = false;
        isOrganizingQueued.value = isQueued;
        if (isQueued) {
          stopVirtualProgress(virtualProgress);
          generationProgress.value = Math.max(96, generationProgress.value);
        } else {
          startTransferVirtualProgress(virtualProgress);
        }
        break;
      }
      case 'done': {
        stopVirtualProgress(virtualProgress);
        clearGenerationQueuedFlags();
        generationPhase.value = 'done';
        generationProgress.value = 100;
        break;
      }
      default:
        break;
    }
  };

  const resolveProgressErrorSource = (snapshot: StoryProgressSnapshot): ServiceErrorSource => {
    if (snapshot.bStatus === -9) return 'images';
    if (snapshot.bStatus === -8) return 'complete';
    if (snapshot.stage === 'image' || snapshot.stage === 'transfer') return 'images';
    return 'complete';
  };

  /** get-progress 成功后尚无完整 story 结果时，用大纲拼一份本地占位 story */
  const buildStoryBookFromOutline = (
    outline: StoryOutline,
    request?: StoryCreatePayload | null
  ): StoryBook => ({
    title: outline.title,
    language: outline.language,
    language_code: outline.language_code,
    theme: outline.theme,
    target_age: outline.target_age,
    story_type: outline.story_type,
    art_style: request?.art_style ?? '',
    art_style_keywords: undefined,
    style_ref_image_url: request?.style_ref_image_url,
    moral: outline.moral,
    characters: outline.characters ?? [],
    pages: (outline.pages ?? []).map((page) => ({
      page: page.page,
      narration: page.narration,
      scene_description: undefined
    }))
  });

  const finalizeGenerationSuccess = (options: {
    projectId: string | number;
    storyBook: StoryBook;
    uLink?: string;
    linkName?: string;
    startedAt?: number;
  }) => {
    const { projectId, storyBook, uLink, linkName, startedAt } = options;
    workULink.value = uLink;
    workLinkName.value = linkName;
    updateStory(storyBook, projectId);
    pendingCompleteProjectId.value = undefined;
    finalImageResult.value = { story: storyBook };
    sessionStorage.setItem('storybook:test-preview', buildPreviewStoragePayload());
    generationProgress.value = 100;
    generationDone.value = true;
    generationPhase.value = 'done';
    clearGenerationQueuedFlags();
    generationTtsStatus.value = 'success';
    ttsRetrying.value = false;
    ttsRetryContext.value = null;
    pendingBookGeneration.value = null;
    createBookRetryCount.value = 0;
    void balanceStore.refreshBalance();
    if (startedAt !== undefined) {
      console.info(`[storybook] 绘本整体生成耗时：${formatDuration(startedAt)}`);
    }
  };

  /** 轮询 get-progress；失败时展示重试弹窗并返回 null */
  const waitStoryGenerationProgress = async (
    projectId: string | number,
    virtualProgress?: VirtualProgressController | null
  ): Promise<StoryProgressSnapshot | null> => {
    const progressSnapshot = await pollStoryProgress(projectId, {
      intervalMs: 3000,
      onUpdate: (snapshot) => applyGenerationProgressFromStory(snapshot, virtualProgress)
    });
    clearGenerationQueuedFlags();
    stopVirtualProgress(virtualProgress);

    if (progressSnapshot.status === 'failed' || progressSnapshot.stage === 'failed') {
      showServiceError('busy', resolveProgressErrorSource(progressSnapshot));
      return null;
    }
    return progressSnapshot;
  };

  /** 调用 /retry 后继续 get-progress */
  const resumeGenerationAfterRetry = async (projectId: string | number) => {
    const outline = syncOutlineNarration() ?? storyOutline.value;
    if (!outline || !storyRequest.value) {
      ElMessage.error('无法重试，请重新生成绘本');
      return;
    }

    generatingBook.value = true;
    generationModalVisible.value = true;
    generationDone.value = false;
    generationPhase.value = 'complete';
    generationProgress.value = 0;
    clearGenerationQueuedFlags();
    completeServiceErrorVisible.value = false;
    pendingCompleteProjectId.value = projectId;

    const virtualProgress = startCompleteStoryProgress();
    try {
      await retryStory(projectId);
      const progressSnapshot = await waitStoryGenerationProgress(projectId, virtualProgress);
      if (!progressSnapshot) return;

      finalizeGenerationSuccess({
        projectId,
        storyBook: buildStoryBookFromOutline(outline, storyRequest.value),
        uLink: workULink.value,
        linkName: workLinkName.value,
        startedAt: virtualProgress.startTime
      });
    } catch (error: unknown) {
      stopVirtualProgress(virtualProgress);
      clearGenerationQueuedFlags();
      if (isLoginExpiredError(error) || getApiBusinessCode(error) === 'LOGIN_TIMEOUT') {
        generationModalVisible.value = false;
        ElMessage.warning('用户登录已过期');
        showLogin();
        return;
      }
      // retry 失败：继续展示重试弹窗；不可重试仅提示，不关闭弹窗
      if (getApiBusinessCode(error) === 'RETRY_STATUS_NOT_RETRYABLE') {
        generationModalVisible.value = false;
        completeServiceErrorVisible.value = true;
        ElMessage.warning(getErrorMessage(error, '当前状态不支持重试'));
        return;
      }
      showServiceError(classifyServiceError(error) ?? 'busy', serviceErrorSource.value);
    } finally {
      stopVirtualProgress(virtualProgress);
      generatingBook.value = false;
    }
  };

  const backToScriptStep = () => {
    story.value = null;
    storyProjectId.value = undefined;
    pendingCompleteProjectId.value = undefined;
    finalImageResult.value = null;
    workULink.value = undefined;
    workLinkName.value = undefined;
    generationDone.value = false;
    createBookRetryCount.value = 0;
    currentStep.value = 'script';
  };

  const handleCreateNewBook = () => {
    resetOutlineView();
    storyRequest.value = null;
    finalImageResult.value = null;
    generationDone.value = false;
    generationModalVisible.value = false;
    generationPhase.value = 'complete';
    generationProgress.value = 0;
    generationTtsStatus.value = 'success';
    ttsRetrying.value = false;
    ttsRetryContext.value = null;
    pendingBookGeneration.value = null;
    createBookRetryCount.value = 0;
    form.value.description = '';
    sessionStorage.removeItem('storybook:test-preview');
    currentStep.value = 'create';
  };

  const handleStyleFooterAction = () => {
    if (isBookGenerated.value) {
      handleCreateNewBook();
      return;
    }
    createBookRetryCount.value = 0;
    handleCreateBook();
  };

  const buildPreviewStoragePayload = () =>
    JSON.stringify({
      story: story.value,
      result: finalImageResult.value,
      uLink: workULink.value,
      linkName: workLinkName.value
    });

  const buildTtsVoicePayload = (voice: VoiceOption, volume: number, speed: number) => ({
    roleId: voice.id,
    source: voice.provider,
    pitchRate: 0,
    speed,
    volume,
    name: voice.name,
    description: voice.description || undefined,
    portrait: voice.portrait || undefined,
    category: voice.category || undefined
  });

  /** TTS pages：封面页 page:0 传书名，其后为各页旁白 */
  const buildCreateTtsPages = (
    title: string,
    pages: Array<{ page: number; narration?: string }>
  ): CreateTtsPageInput[] => [
    { page: 0, text: (title ?? '').trim() },
    ...(pages ?? []).map((page) => ({
      page: page.page,
      text: (page.narration ?? '').trim()
    }))
  ];

  const buildCreateMusicPayload = (): StoryCompleteMusicPayload | null => {
    if (selectedMusicId.value === null || !selectedMusicUrl.value) return null;
    return {
      url: selectedMusicUrl.value,
      name: selectedMusicName.value || '背景音乐',
      volume: Math.round(musicVolume.value * 100),
      model: ''
    };
  };

  const applyTtsAudioToStory = (
    targetStory: StoryBook,
    ttsPages: CreateTtsPageInput[],
    audioList: TtsAudioItem[]
  ): StoryBook => {
    const audioByPage = new Map<number, string>();
    ttsPages.forEach((item, index) => {
      if (!item) return;
      const audioUrl = audioList[index]?.url?.trim();
      if (audioUrl) audioByPage.set(item.page, audioUrl);
    });

    return {
      ...targetStory,
      pages: (targetStory.pages ?? []).map((page) => {
        const audioUrl = audioByPage.get(page.page);
        return audioUrl ? { ...page, audio_url: audioUrl } : page;
      })
    };
  };

  const normalizeTtsAudioList = (audioList: TtsAudioItem[], length: number): TtsAudioItem[] => {
    const normalized = Array.from({ length }, (_, index) => {
      const item = audioList[index];
      // 与 pages 等长占位：保留原 page，缺省时用下标（封面为 0）
      return {
        page: item?.page ?? index,
        text: item?.text ?? '',
        url: item?.url?.trim() ?? ''
      };
    });
    return normalized;
  };

  const analyzeTtsStatus = (
    pages: CreateTtsPageInput[],
    audioList: TtsAudioItem[]
  ): GenerationTtsStatus => {
    let expected = 0;
    let succeeded = 0;

    pages.forEach((page, index) => {
      if (!page || !page.text.trim()) return;
      expected += 1;
      if (audioList[index]?.url?.trim()) succeeded += 1;
    });

    if (expected === 0 || succeeded >= expected) return 'success';
    if (succeeded === 0) return 'all-failed';
    return 'partial-failed';
  };

  const buildTtsRetryPages = (
    pages: CreateTtsPageInput[],
    audioList: TtsAudioItem[]
  ): CreateTtsPageInput[] =>
    pages.map((page, index) => {
      if (!page) return null;
      if (!page.text.trim()) return null;
      if (audioList[index]?.url?.trim()) return null;
      return page;
    });

  const mergeTtsRetryAudio = (
    pages: CreateTtsPageInput[],
    previousAudio: TtsAudioItem[],
    retryPages: CreateTtsPageInput[],
    retryAudio: TtsAudioItem[]
  ): TtsAudioItem[] => {
    const merged = normalizeTtsAudioList(previousAudio, pages.length);

    // 与原始 pages 等长：按下标对齐，null 占位页保留原音频
    if (retryAudio.length === pages.length) {
      pages.forEach((page, index) => {
        if (retryPages[index] === null) return;
        const url = retryAudio[index]?.url?.trim();
        if (url) {
          merged[index] = {
            page: retryAudio[index]?.page ?? page?.page ?? merged[index].page,
            text: retryAudio[index]?.text ?? page?.text ?? '',
            url
          };
        }
      });
      return merged;
    }

    // 紧凑返回：仅含需重试页，按非 null 顺序合并
    let retryIndex = 0;
    pages.forEach((page, index) => {
      if (retryPages[index] === null) return;
      const item = retryAudio[retryIndex++];
      const url = item?.url?.trim();
      if (url) {
        merged[index] = {
          page: item?.page ?? page?.page ?? merged[index].page,
          text: item?.text ?? page?.text ?? '',
          url
        };
      }
    });
    return merged;
  };

  // const updateImageGenerationProgress = (imageProgress: number) => {
  //   if (imageProgress < 60) {
  //     generationPhase.value = 'images';
  //     generationProgress.value = Math.min(60, 30 + Math.round((imageProgress * 30) / 60));
  //     return;
  //   }

  //   if (imageProgress < 80) {
  //     generationPhase.value = 'voice';
  //     generationProgress.value = Math.min(80, 60 + Math.round(((imageProgress - 60) * 20) / 20));
  //     return;
  //   }

  //   generationPhase.value = 'organizing';
  //   generationProgress.value = Math.min(99, 80 + Math.round(((imageProgress - 80) * 19) / 20));
  // };

  const resolveTtsPollResult = (
    taskId: string,
    task: Awaited<ReturnType<typeof pollTask<TtsTaskResult>>> | null
  ): CreateTtsResult | null => {
    if (!task) {
      return {
        taskId,
        audio: [],
        failed_pages: [],
        task: {
          task_id: taskId,
          task_type: 'create-tts',
          status: 'failed',
          progress: 0,
          stage: '',
          message: '',
          result: null,
          created_at: 0,
          updated_at: 0,
          metadata: {}
        }
      };
    }

    const result = task.status === 'succeeded' ? task.result : null;
    return {
      taskId,
      audio: Array.isArray(result?.audio) ? result.audio : [],
      failed_pages: result?.failed_pages ?? [],
      task
    };
  };

  const finalizeBookGeneration = (
    context: PendingBookGeneration,
    imageTask: Awaited<ReturnType<typeof pollTask<ImageGenerateResult>>>
  ) => {
    const mergedStory = imageTask.result?.story ?? context.storyForImages;
    finalImageResult.value = {
      ...imageTask.result,
      story: mergedStory
    };
    story.value = mergedStory;
    sessionStorage.setItem('storybook:test-preview', buildPreviewStoragePayload());
    generationProgress.value = 100;
    generationDone.value = true;
    generationPhase.value = 'done';
    isImagesQueued.value = false;
    // 配音已由 /complete 的 tts 参数交由服务端处理，前端不再单独轮询 create-tts
    generationTtsStatus.value = 'success';
    ttsRetrying.value = false;
    ttsRetryContext.value = null;
    pendingBookGeneration.value = null;
  };

  const runBookGeneration = async (context: PendingBookGeneration) => {
    generationPhase.value = 'images';
    generationProgress.value = Math.max(generationProgress.value, 30);
    isImagesQueued.value = false;

    // TODO: 暂时屏蔽 /images 调用，恢复时取消下方注释并删除临时跳过逻辑
    // const imageStartTime = performance.now();

    // const imageSubmitted = await generateImages({
    //   id: context.projectId,
    //   story: context.storyForImages,
    //   layout: context.imageLayout,
    //   aspect_ratio: context.imageAspectRatio,
    //   page_aspect_ratio: context.imageAspectRatio,
    //   character_aspect_ratio: context.imageCharacterAspectRatio,
    //   cover_aspect_ratio: context.imageCoverAspectRatio,
    //   image_provider: context.imageProvider,
    //   phases: context.phases
    // }).catch((error: unknown) => {
    //   const kind = classifyServiceError(error) ?? 'busy';
    //   throw createImageServiceError(kind, getErrorMessage(error, '绘本画面生成失败，请稍后重试'));
    // });

    // const imageTask = await pollTask<ImageGenerateResult>(imageSubmitted.task_id, {
    //   intervalMs: 5000,
    //   onUpdate: (snapshot) => {
    //     if (snapshot.status === 'queued') {
    //       isImagesQueued.value = true;
    //       generationPhase.value = 'images';
    //       return;
    //     }
    //     if (isImagesQueued.value) {
    //       isImagesQueued.value = false;
    //     }
    //     updateImageGenerationProgress(snapshot.progress ?? 0);
    //   }
    // })
    //   .then((task) => {
    //     if (task.status === 'failed') {
    //       throw createImageServiceError('busy', task.error || '绘本画面生成失败，请稍后重试');
    //     }
    //     return task;
    //   })
    //   .catch((error: unknown) => {
    //     if (isImageServiceError(error)) throw error;
    //     const kind = classifyServiceError(error) ?? 'busy';
    //     throw createImageServiceError(kind, getErrorMessage(error, '绘本画面生成失败，请稍后重试'));
    //   });

    // console.info(`[storybook] 生图完成耗时：${formatDuration(imageStartTime)}`);

    // finalizeBookGeneration(context, imageTask);

    // 临时：跳过 /images，直接以 complete 返回的 story 结束生成流程
    finalizeBookGeneration(context, {
      task_id: '',
      task_type: 'images',
      status: 'succeeded',
      progress: 100,
      stage: '',
      message: '',
      result: { story: context.storyForImages },
      created_at: 0,
      updated_at: 0,
      metadata: {}
    });
  };

  const retryFailedTts = async () => {
    const context = ttsRetryContext.value;
    if (!context || ttsRetrying.value) return;

    const retryPages = buildTtsRetryPages(context.payload.pages, context.audio);
    if (!retryPages.some((page) => page !== null)) {
      generationTtsStatus.value = 'success';
      ttsRetryContext.value = null;
      return;
    }

    ttsRetrying.value = true;
    try {
      const retryPayload: CreateTtsPayload = {
        ...context.payload,
        pages: retryPages
      };
      const submitted = await submitCreateTts(retryPayload);
      const task = await pollTask<TtsTaskResult>(submitted.taskId, { intervalMs: 3000 });
      const ttsResult = resolveTtsPollResult(submitted.taskId, task);
      const mergedAudio = mergeTtsRetryAudio(
        context.payload.pages,
        context.audio,
        retryPages,
        ttsResult?.audio ?? []
      );
      const ttsStatus = analyzeTtsStatus(context.payload.pages, mergedAudio);
      const baseStory = finalImageResult.value?.story ?? story.value;
      if (baseStory) {
        const mergedStory = applyTtsAudioToStory(baseStory, context.payload.pages, mergedAudio);
        story.value = mergedStory;
        if (finalImageResult.value) {
          finalImageResult.value = {
            ...finalImageResult.value,
            story: mergedStory,
            tts_audio: mergedAudio
          };
        }
        sessionStorage.setItem('storybook:test-preview', buildPreviewStoragePayload());
      }

      generationTtsStatus.value = ttsStatus;
      if (ttsStatus === 'success') {
        ttsRetryContext.value = null;
      } else {
        ttsRetryContext.value = {
          payload: context.payload,
          audio: mergedAudio
        };
      }
    } catch (error: unknown) {
      console.warn('[storybook] 配音重试失败:', error);
      ElMessage.error(getErrorMessage(error, '配音重试失败，请稍后重试'));
    } finally {
      ttsRetrying.value = false;
    }
  };

  const handleCreateBook = async () => {
    const outline = syncOutlineNarration();
    if (!story.value && (!outline || !storyRequest.value)) {
      ElMessage.warning('请先生成故事大纲');
      return;
    }

    if (generatingBook.value || checkingBookBalance.value) return;

    const requiredCoins = estimatedTotalPrice.value;
    if (requiredCoins > 0) {
      checkingBookBalance.value = true;
      try {
        const balanceLoaded = await balanceStore.refreshBalance();
        if (!isCreatePageActive.value) return;
        if (balanceLoaded && balanceStore.balance < requiredCoins) {
          insufficientCoinsModalVisible.value = true;
          return;
        }
      } finally {
        checkingBookBalance.value = false;
      }
    }

    generatingBook.value = true;
    generationModalVisible.value = true;
    generationDone.value = false;
    generationPhase.value = 'complete';
    generationProgress.value = 0;
    clearGenerationQueuedFlags();
    generationTtsStatus.value = 'success';
    ttsRetrying.value = false;
    ttsRetryContext.value = null;
    completeServiceErrorVisible.value = false;
    pendingBookGeneration.value = null;
    finalImageResult.value = null;

    try {
      let storyForImages = story.value;
      let projectId = storyProjectId.value;

      if (!storyForImages && outline && storyRequest.value) {
        const completeProgress = startCompleteStoryProgress();
        try {
          const leftoverProjectId = pendingCompleteProjectId.value;
          if (leftoverProjectId !== undefined && leftoverProjectId !== '') {
            pendingCompleteProjectId.value = undefined;
            try {
              await deleteStory(leftoverProjectId);
            } catch (error) {
              console.warn('[storybook] 重试前删除上次 complete 工程失败:', error);
            }
          }

          if (!selectedVoice.value.id) {
            await initDefaultVoice();
          }
          if (!selectedVoice.value.id) {
            throw new Error('请选择配音角色');
          }

          const completeImageLayout = storyRequest.value?.layout ?? currentLayoutOption.value.value;
          const completeImageAspectRatio =
            storyRequest.value?.page_aspect_ratio ??
            storyRequest.value?.aspect_ratio ??
            currentLayoutOption.value.page_aspect_ratio;
          const completeImageCharacterAspectRatio =
            storyRequest.value?.character_aspect_ratio ??
            currentLayoutOption.value.character_aspect_ratio;
          const completeImageCoverAspectRatio =
            storyRequest.value?.cover_aspect_ratio ?? currentLayoutOption.value.cover_aspect_ratio;
          const completeImagePhases: ImagePhase[] = shouldGenerateCharacterPreviews({
            characters: outline.characters ?? []
          } as StoryBook)
            ? ['character_previews', 'cover', 'pages']
            : ['cover', 'pages'];

          const completeSubmitted = await completeStoryFromOutline({
            request: storyRequest.value,
            outline,
            tts: {
              voice: buildTtsVoicePayload(selectedVoice.value, voiceVolume.value, voiceSpeed.value),
              pages: buildCreateTtsPages(outline.title, outline.pages ?? [])
            },
            music: buildCreateMusicPayload() ?? undefined,
            images: {
              layout: completeImageLayout,
              aspect_ratio: completeImageAspectRatio,
              page_aspect_ratio: completeImageAspectRatio,
              character_aspect_ratio: completeImageCharacterAspectRatio,
              cover_aspect_ratio: completeImageCoverAspectRatio,
              image_provider: selectedImageProvider.value,
              phases: completeImagePhases
            }
          });
          const submittedProjectId = completeSubmitted.id;
          if (submittedProjectId === undefined || submittedProjectId === '') {
            // 无工程 id，只能重新调 /complete，不能走 /retry
            throw createCompleteServiceError('busy', '完整故事生成失败：未返回工程 ID');
          }
          pendingCompleteProjectId.value = submittedProjectId;
          workULink.value = completeSubmitted.uLink;
          workLinkName.value = completeSubmitted.linkName;

          // story 阶段继续跑虚拟进度；进入 image 后由 get-progress 的 progress 接管
          const progressSnapshot = await waitStoryGenerationProgress(
            submittedProjectId,
            completeProgress
          );
          if (!progressSnapshot) {
            // 已展示重试弹窗，保留工程 id 供 /retry
            return;
          }

          finalizeGenerationSuccess({
            projectId: submittedProjectId,
            storyBook: buildStoryBookFromOutline(outline, storyRequest.value),
            uLink: completeSubmitted.uLink,
            linkName: completeSubmitted.linkName,
            startedAt: completeProgress.startTime
          });
          return;
        } catch (error: unknown) {
          clearGenerationQueuedFlags();
          // 注意：pendingCompleteProjectId 仅在 /complete 成功返回 id 后才写入。
          // 因此此处若无 id，重试会重新调 /complete；有 id 则走 /retry。
          if (isCompleteServiceError(error)) throw error;
          if (isCompleteLimitError(error)) throw error;
          if (isLoginExpiredError(error)) throw error;
          if (isTextScanFailedError(error)) throw error;

          const businessCode = getApiBusinessCode(error);
          if (businessCode === 'LOGIN_TIMEOUT') {
            throw createLoginExpiredError(getErrorMessage(error, '用户登录已过期'));
          }
          if (businessCode === 'REACH_LIMIT' || businessCode === 'INSUFFICIENT_BALANCE') {
            throw createCompleteLimitError(
              businessCode,
              getErrorMessage(error, '完整故事生成失败，请稍后重试')
            );
          }

          const kind = classifyServiceError(error);
          if (kind) throw createCompleteServiceError(kind, getErrorMessage(error, ''));
          // 其余 complete 调用失败（网络/服务器无响应/业务码非 OK）→ 弹窗后重新调 /complete
          throw createCompleteServiceError(
            'busy',
            getErrorMessage(error, '完整故事生成失败，请稍后重试')
          );
        } finally {
          stopVirtualProgress(completeProgress);
        }
      }

      if (!storyForImages) throw new Error('请先生成故事内容');

      if (!selectedVoice.value.id) {
        await initDefaultVoice();
      }
      if (!selectedVoice.value.id) {
        throw new Error('请选择配音角色');
      }

      const phases: ImagePhase[] = shouldGenerateCharacterPreviews(storyForImages)
        ? ['character_previews', 'cover', 'pages']
        : ['cover', 'pages'];
      const imageLayout = storyRequest.value?.layout ?? currentLayoutOption.value.value;
      const imageAspectRatio =
        storyRequest.value?.page_aspect_ratio ??
        storyRequest.value?.aspect_ratio ??
        currentLayoutOption.value.page_aspect_ratio;
      const imageCharacterAspectRatio =
        storyRequest.value?.character_aspect_ratio ??
        currentLayoutOption.value.character_aspect_ratio;
      const imageCoverAspectRatio =
        storyRequest.value?.cover_aspect_ratio ?? currentLayoutOption.value.cover_aspect_ratio;
      pendingBookGeneration.value = {
        storyForImages,
        projectId,
        phases,
        imageLayout,
        imageAspectRatio,
        imageCharacterAspectRatio,
        imageCoverAspectRatio,
        imageProvider: selectedImageProvider.value
      };

      try {
        await runBookGeneration(pendingBookGeneration.value);
      } catch (error: unknown) {
        isImagesQueued.value = false;
        if (isImageServiceError(error)) {
          showServiceError(error.kind, 'images');
          return;
        }
        throw error;
      }
    } catch (error: unknown) {
      clearGenerationQueuedFlags();
      if (isLoginExpiredError(error)) {
        generationModalVisible.value = false;
        ElMessage.warning('用户登录已过期');
        showLogin();
        return;
      }
      if (isCompleteLimitError(error)) {
        generationModalVisible.value = false;
        if (error.code === 'REACH_LIMIT') {
          isStorageLimitVisible.value = true;
        } else {
          void balanceStore.refreshBalance();
          insufficientCoinsModalVisible.value = true;
        }
        return;
      }
      if (isTextScanFailedError(error)) {
        generationModalVisible.value = false;
        showTextScanFailedError();
        return;
      }
      if (isCompleteServiceError(error)) {
        showServiceError(error.kind, 'complete');
        return;
      }
      generationModalVisible.value = false;
      ElMessage.error(getErrorMessage(error, '绘本生成失败，请稍后重试'));
    } finally {
      generatingBook.value = false;
    }
  };

  const showServiceError = (kind: CompleteServiceErrorKind, source: ServiceErrorSource) => {
    clearGenerationQueuedFlags();
    generationModalVisible.value = false;
    serviceErrorSource.value = source;
    completeServiceErrorKind.value = kind;
    completeServiceErrorVisible.value = true;
  };

  const closeCompleteServiceError = () => {
    completeServiceErrorVisible.value = false;
  };

  const retryImageGeneration = async () => {
    const context = pendingBookGeneration.value;
    if (!context) {
      ElMessage.error('无法重试生图，请重新生成绘本');
      return;
    }

    generatingBook.value = true;
    generationModalVisible.value = true;
    generationDone.value = false;
    generationPhase.value = 'images';
    generationProgress.value = Math.max(generationProgress.value, 30);
    generationTtsStatus.value = 'success';
    ttsRetrying.value = false;

    try {
      await runBookGeneration(context);
    } catch (error: unknown) {
      if (isImageServiceError(error)) {
        showServiceError(error.kind, 'images');
        return;
      }
      generationModalVisible.value = false;
      ElMessage.error(getErrorMessage(error, '绘本生成失败，请稍后重试'));
    } finally {
      generatingBook.value = false;
    }
  };

  // 全部图片生成完成后同步生图阶段产生的云币消耗。
  void balanceStore.refreshBalance();

  const retryServiceError = async () => {
    if (generatingBook.value) return;
    createBookRetryCount.value += 1;
    closeCompleteServiceError();

    // 已有 complete 返回的工程 id（get-progress 失败）→ 走 /retry
    const retryProjectId = pendingCompleteProjectId.value;
    if (retryProjectId !== undefined && retryProjectId !== '') {
      await resumeGenerationAfterRetry(retryProjectId);
      return;
    }

    // 无工程 id（/complete 本身失败：网络/服务器错误等）→ 重新调用 /complete
    if (serviceErrorSource.value === 'images') {
      await retryImageGeneration();
      return;
    }

    await handleCreateBook();
  };

  const closeGenerationModal = () => {
    generationModalVisible.value = false;
    ttsRetrying.value = false;
    pendingBookGeneration.value = null;
  };

  const goStoryList = () => {
    completeServiceErrorVisible.value = false;
    router.push({ name: 'story' });
  };

  const goEditBook = () => {
    router.push({ name: 'edit' });
  };

  /**
   * 新窗口打开阅读页（与故事列表「阅读」一致）
   * @param url 阅读页地址或发布目录
   * @param fromReadButton 是否来自「阅读」按钮（用于错误页回编窗口名）
   */
  // const openReaderWindow = (url: string, fromReadButton: boolean) => {
  //   // 步骤 1：非阅读按钮入口，普通新标签打开
  //   if (!fromReadButton) {
  //     window.open(url, '_blank', 'noopener');
  //     return;
  //   }

  //   // 步骤 2：阅读按钮入口，使用可回编的窗口名并断开 opener
  //   const readerWindow = window.open(url, createPageErrorEditWindowName());
  //   if (readerWindow) {
  //     readerWindow.opener = null;
  //   }
  // };

  /** 生成完成后阅读：已转存打开发布目录，未转存用预览弹窗 */
  const goReadBook = async () => {
    if (!storyProjectId.value) {
      router.push({ name: 'story' });
      return;
    }

    const projectId = String(storyProjectId.value);

    try {
      // 步骤 1：解析并尝试写回，按转存状态分流
      const result = await prepareBookForRead(projectId);

      await projectManager.loadPreview(result.data);
      previewVisible.value = true;

      // 步骤 2：未转存完 → 内存 hydrate + 预览弹窗
      // if (result.mode === 'preview') {
      //   await projectManager.loadPreview(result.data);
      //   previewVisible.value = true;
      //   return;
      // }

      // 步骤 3：已转存 → 打开发布目录；无 shareUrl 时走应用内阅读路由
      // const shareUrl = result.data.project?.shareUrl;
      // if (shareUrl) {
      //   const folder = shareUrl.replace(/[^/]+$/, '');
      //   openReaderWindow(folder, true);
      //   return;
      // }

      // openReaderWindow(router.resolve({ name: 'book', params: { id: projectId } }).href, true);
    } catch (error: unknown) {
      ElMessage.error(getErrorMessage(error, '打开阅读失败，请稍后重试'));
    }
  };
</script>

<style scoped>
  .create-page {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    align-items: flex-start;
    justify-content: safe center;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 24px 40px 32px;
    box-sizing: border-box;
    background: #fbf9ff;
    user-select: none;
    -webkit-user-select: none;
    cursor: default;
  }

  /* 仅可编辑区域允许选中与文本光标 */
  .create-page :is(input, textarea, [contenteditable='true']),
  .mention-popup-root--mobile :is(input, textarea, [contenteditable='true']) {
    user-select: text;
    -webkit-user-select: text;
    cursor: text;
  }

  .mention-popup-root--mobile {
    user-select: none;
    -webkit-user-select: none;
  }

  .create-page.flow-page {
    align-items: safe center;
    justify-content: safe center;
    padding: 32px 48px 32px;
    overflow-y: auto;
  }

  .create-page::before {
    position: absolute;
    inset: 0;
    content: '';
    opacity: 0.42;
    pointer-events: none;
    background-image: radial-gradient(rgba(124, 86, 255, 0.13) 1px, transparent 1px);
    background-size: 12px 12px;
  }

  .create-page::after {
    position: absolute;
    width: 760px;
    height: 360px;
    left: 50%;
    top: 50%;
    content: '';
    transform: translate(-50%, -17%);
    border-radius: 999px;
    pointer-events: none;
    background: radial-gradient(ellipse, rgba(128, 87, 255, 0.12), transparent 66%);
    filter: blur(22px);
  }

  .page-actions {
    position: absolute;
    top: 17px;
    right: 26px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .upgrade-btn {
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 18px;
    border: 1px solid rgba(245, 182, 76, 0.58);
    border-radius: 10px;
    background: linear-gradient(180deg, #ffe8b9 0%, #ffd58d 100%);
    color: #d17816;
    font-size: 16px;
    font-weight: 800;
    box-shadow: 0 8px 20px rgba(227, 147, 31, 0.12);
    cursor: pointer;
  }

  .vip-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    object-fit: contain;
  }

  .credits-pill {
    height: 34px;
    min-width: 76px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 12px;
    box-sizing: border-box;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    color: #2f2547;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 8px 24px rgba(71, 54, 132, 0.08);
  }

  .spark,
  .btn-spark {
    color: #7c3cff;
    text-shadow: 1px 1px 0 #ff74bb;
  }

  .btn-spark-img {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    object-fit: contain;
  }

  .avatar-btn {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, #2a2233, #efb0a2);
    box-shadow: 0 10px 26px rgba(71, 54, 132, 0.15);
    cursor: pointer;
  }

  .avatar-btn span {
    font-size: 23px;
    transform: translateY(1px);
  }

  .create-panel,
  .flow-panel {
    position: relative;
    z-index: 1;
  }

  .create-panel {
    width: min(780px, 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .title-icon {
    margin-bottom: 8px;
    line-height: 1;
  }

  .title-icon-img {
    display: block;
    width: 35px;
    height: 35px;
    object-fit: contain;
  }

  .create-title {
    width: 270px;
    height: 36px;
    margin: 0 0 24px;
  }

  .create-title__svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  }

  .prompt-card {
    width: 100%;
    height: 185px;
    display: flex;
    flex-direction: column;
    padding: 18px 16px 15px 17px;
    box-sizing: border-box;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 20px 70px rgba(72, 55, 133, 0.08);
  }

  .mention-popup {
    --mention-scale: 1;
    position: absolute;
    left: calc(100% + 10px);
    top: -10px;
    z-index: 30;
    width: 429px;
    height: 436px;
    max-width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
    padding: calc(16px * var(--mention-scale));
    border: 1px solid #ebe7f8;
    border-radius: calc(14px * var(--mention-scale));
    background: #fff;
    box-shadow: 0 16px 48px rgba(72, 55, 133, 0.16);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: calc(12px * var(--mention-scale));
  }

  .mention-loading,
  .mention-empty {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #7a7787;
    font-size: calc(13px * var(--mention-scale));
  }

  .mention-grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: calc(10px * var(--mention-scale));
    overflow-y: auto;
    padding-right: 2px;
    align-content: start;
  }

  .mention-grid::-webkit-scrollbar {
    width: 4px;
  }

  .mention-grid::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: rgba(103, 48, 231, 0.25);
  }

  .mention-char-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: calc(8px * var(--mention-scale)) calc(6px * var(--mention-scale))
      calc(10px * var(--mention-scale));
    border: 2px solid transparent;
    border-radius: calc(10px * var(--mention-scale));
    background: #f8f9ff;
    cursor: pointer;
    transition:
      border-color 0.14s ease,
      background 0.14s ease;
  }

  .mention-char-card:hover,
  .mention-char-card.active {
    border-color: #6730e7;
    background: #f3f0ff;
  }

  .mention-char-img {
    width: calc(72px * var(--mention-scale));
    height: calc(72px * var(--mention-scale));
    object-fit: contain;
  }

  .mention-char-placeholder {
    width: calc(72px * var(--mention-scale));
    height: calc(72px * var(--mention-scale));
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(135deg, #a78bfa, #7c3aed);
    color: #fff;
    font-size: calc(18px * var(--mention-scale));
    font-weight: 700;
  }

  .mention-char-name {
    margin-top: calc(6px * var(--mention-scale));
    color: #160211;
    font-size: calc(14px * var(--mention-scale));
    font-weight: 800;
    line-height: 1.2;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mention-char-setting {
    margin-top: calc(4px * var(--mention-scale));
    color: #7a7787;
    font-size: calc(12px * var(--mention-scale));
    line-height: 1.3;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mention-create-btn {
    flex-shrink: 0;
    width: 100%;
    height: calc(44px * var(--mention-scale));
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc(6px * var(--mention-scale));
    border: 0;
    border-radius: calc(12px * var(--mention-scale));
    background: linear-gradient(90deg, #6730e7, #6931e6);
    color: #fff;
    font-size: calc(14px * var(--mention-scale));
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 8px 20px rgba(105, 49, 230, 0.18);
  }

  .mention-create-btn .btn-spark {
    color: #fff;
    text-shadow: 1px 1px 0 #ff86bf;
  }

  .mention-create-btn:hover {
    opacity: 0.92;
  }

  .prompt-input {
    flex: 1;
    width: 100%;
    padding: 0;
    border: 0;
    outline: 0;
    resize: none;
    background: transparent;
    color: #1d1a2c;
    font: inherit;
    font-size: 16px;
    line-height: 1.7;
  }

  .prompt-input::placeholder {
    color: #b5afc3;
  }

  .prompt-footer {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
  }

  .prompt-footer-leading {
    flex: 1;
    min-width: 0;
  }

  .mention-wrap {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 31;
    width: max-content;
  }

  .mention-btn {
    width: 35px;
    height: 35px;
    border: 0;
    border-radius: 50%;
    background: #f1f2f5;
    color: #707480;
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;
  }

  .mention-tip {
    position: absolute;
    left: 43px;
    top: 50%;
    height: 28px;
    display: inline-flex;
    align-items: center;
    padding: 0 11px;
    border: 0;
    border-radius: 8px;
    background: #111;
    color: #fff;
    font: inherit;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    cursor: pointer;
    transform: translateY(-50%) translateX(-4px);
    transition:
      opacity 0.16s ease,
      transform 0.16s ease,
      visibility 0.16s ease;
  }

  .mention-wrap:hover .mention-tip,
  .mention-tip--persistent {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(-50%) translateX(0);
  }

  .mention-tip::before {
    width: 7px;
    height: 7px;
    margin-left: -14px;
    margin-right: 7px;
    content: '';
    background: #111;
    transform: rotate(45deg);
  }

  .count-text {
    color: #7a7787;
    font-size: 13px;
  }

  .options-grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 14px;
    margin-top: 21px;
  }

  .option-field {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .option-label {
    color: #7c768b;
    font-size: 13px;
    font-weight: 600;
  }

  .option-select {
    width: 100%;
    height: 43px;
    padding: 0 16px;
    border: 1px solid rgba(229, 227, 239, 0.92);
    border-radius: 14px;
    outline: 0;
    appearance: none;
    background: rgba(255, 255, 255, 0.96);
    color: #15121f;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 8px 22px rgba(76, 62, 124, 0.04);
    cursor: pointer;
  }

  .option-trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    text-align: left;
    font: inherit;
  }

  .option-trigger span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .option-chevron {
    flex-shrink: 0;
    color: #160211;
  }

  .generate-btn,
  .next-btn {
    border: 0;
    background: linear-gradient(90deg, #6730e7, #6730e7 48%, #6931e6);
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.1px;
    cursor: pointer;
    box-shadow: 0 16px 34px rgba(105, 49, 230, 0.22);
  }

  .generate-btn {
    width: 100%;
    height: 43px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    margin-top: 20px;
    border-radius: 14px;
  }

  .generate-btn:disabled,
  .next-btn:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .generate-btn .btn-spark {
    color: #fff;
    text-shadow: 1px 1px 0 #ff86bf;
  }

  .generate-btn-spark {
    width: 12px;
    height: 12px;
    flex: none;
  }

  .suggest-section {
    width: 100%;
    margin-top: 18px;
    order: 2;
  }

  .options-grid {
    order: 1;
  }

  .generate-btn-wrap {
    order: 3;
  }

  .create-cta-spacer {
    display: none;
  }

  .suggest-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 13px;
    color: #8f899b;
    font-size: 13px;
  }

  .suggest-title-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(224, 220, 235, 0.9));
  }

  .suggest-title-line:last-child {
    background: linear-gradient(90deg, rgba(224, 220, 235, 0.9), transparent);
  }

  .suggest-title-text {
    font-style: normal;
    white-space: nowrap;
  }

  .option-art-preview {
    display: none;
  }

  .generate-btn-wrap {
    width: 100%;
  }

  .mention-popup-root--mobile {
    position: fixed;
    inset: 0;
    z-index: 40;
    pointer-events: none;
  }

  .mention-popup-root--mobile .mention-mobile-overlay,
  .mention-popup-root--mobile .mention-popup {
    pointer-events: auto;
  }

  .mention-mobile-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  .mention-popup-header {
    display: none;
  }

  .mention-popup-close {
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #6e6a7c;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
  }

  .suggest-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 13px;
    margin-top: 17px;
  }

  .suggest-tag {
    height: 34px;
    padding: 0 18px;
    border: 0;
    border-radius: 999px;
    background: rgba(241, 240, 246, 0.92);
    color: #403b4d;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  .suggest-tag:hover {
    background: #ebe7fb;
    color: #6730e7;
  }

  .flow-panel {
    width: min(1000px, 100%);
    display: flex;
    flex-direction: column;
  }

  .script-panel--loading {
    min-height: min(640px, calc(100% - 24px));
  }

  .flow-content {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .flow-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 23px;
  }

  .flow-title-wrap {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }

  .flow-title {
    margin: 0;
    color: #17131f;
    font-size: 19px;
    font-weight: 800;
  }

  .flow-title-wrap span {
    color: #6d687a;
    font-size: 13px;
    font-weight: 700;
  }

  .refresh-btn,
  .back-btn {
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 0 15px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    color: #181421;
    font-size: 13px;
    font-weight: 800;
    box-shadow: 0 8px 22px rgba(76, 62, 124, 0.06);
    cursor: pointer;
  }

  .refresh-btn:disabled,
  .back-btn:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    box-shadow: none;
    color: #8f899b;
  }

  .back-chevron {
    display: inline-flex;
    align-items: center;
    font-size: 20px;
    font-weight: 400;
    line-height: 1;
    transform: translateY(-1px);
  }

  .script-list,
  .storyboard-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .script-list--loading {
    flex: 1;
    min-height: 360px;
    justify-content: center;
  }

  .script-loading {
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 28px;
    color: #6d687a;
  }

  .outline-progress-ring {
    position: relative;
    width: 120px;
    height: 120px;
  }

  .outline-progress-ring svg {
    display: block;
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  .outline-progress-track {
    stroke: #efeaf8;
    stroke-width: 10;
  }

  .outline-progress-fill {
    stroke-width: 10;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.45s ease;
  }

  .outline-progress-value {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #241c34;
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
  }

  .outline-progress-tip {
    margin: 0;
    color: #6e6a7c;
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;
    text-align: center;
  }

  .loading-spinner {
    width: 34px;
    height: 34px;
    border: 3px solid rgba(103, 48, 231, 0.14);
    border-top-color: #7a48f2;
    border-radius: 50%;
    animation: script-loading-spin 0.8s linear infinite;
  }

  .outline-queue-spinner {
    width: 56px;
    height: 56px;
    box-sizing: border-box;
    border: 4px solid rgba(103, 48, 231, 0.14);
    border-top-color: #7a48f2;
    border-radius: 50%;
    animation: script-loading-spin 0.8s linear infinite;
  }

  @keyframes script-loading-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .script-row,
  .storyboard-row {
    display: grid;
    grid-template-columns: 45px 1fr;
    align-items: center;
    gap: 15px;
  }

  .script-row {
    align-items: start;
  }

  .title-row strong {
    color: #6730e7;
  }

  .title-edit {
    min-height: 54px;
    background: rgba(255, 255, 255, 0.92);
    color: #21163d;
    font-size: 17px;
    font-weight: 800;
  }

  .script-row strong,
  .storyboard-row strong {
    color: #17131f;
    font-size: 15px;
    font-weight: 900;
  }

  .script-row strong {
    padding-top: 20px;
  }

  .script-edit {
    width: 100%;
    min-height: 62px;
    height: auto;
    margin: 0;
    padding: 20px 18px;
    border: 0;
    outline: 0;
    resize: none;
    overflow: hidden;
    border-radius: 14px;
    background: #f5f0ff;
    color: #201b2d;
    font: inherit;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    box-sizing: border-box;
  }

  .storyboard-card {
    min-height: 96px;
    display: grid;
    grid-template-columns: 118px 1fr;
    gap: 16px;
    padding: 12px 18px 12px 12px;
    border-radius: 16px;
    background: rgba(241, 235, 255, 0.82);
    box-sizing: border-box;
  }

  .thumb {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    min-height: 72px;
    padding: 8px;
    border-radius: 12px;
    box-sizing: border-box;
  }

  .thumb span {
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.82);
    color: #6531e8;
    font-size: 12px;
    font-weight: 900;
  }

  .visual-copy h3 {
    margin: 4px 0 7px;
    color: #17131f;
    font-size: 15px;
    font-weight: 900;
  }

  .visual-copy p {
    margin: 0;
    color: #403b4d;
    font-size: 14px;
    line-height: 1.7;
  }

  .flow-footer {
    width: 100%;
    display: grid;
    grid-template-columns: 90px 1fr 90px;
    align-items: center;
    margin-top: 40px;
    pointer-events: none;
  }

  .style-footer {
    margin-top: 28px;
  }

  .back-btn {
    grid-column: 1;
    justify-self: start;
    pointer-events: auto;
  }

  .create-book-wrap {
    position: relative;
    grid-column: 2;
    justify-self: center;
    width: 480px;
    pointer-events: auto;
  }

  /* 桥接按钮与定价弹层之间的空隙，避免鼠标移动途中 hover 断开导致弹层消失 */
  .create-book-wrap::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    height: 20px;
  }

  .next-btn {
    grid-column: 2;
    justify-self: center;
    width: 480px;
    max-width: 100%;
    height: 44px;
    border-radius: 14px;
    pointer-events: auto;
  }

  .style-panel {
    position: relative;
    z-index: 1;
    width: 1000px;
    display: flex;
    flex-direction: column;
  }

  .style-content {
    width: 100%;
  }

  .style-title {
    margin: 0 0 20px;
    color: #17131f;
    font-size: 18px;
    font-weight: 800;
  }

  .style-settings {
    display: grid;
    grid-template-columns: 384px 361px;
    gap: 34px;
    align-items: start;
  }

  .layout-setting,
  .config-setting {
    display: flex;
    flex-direction: column;
  }

  .setting-label {
    margin-bottom: 11px;
    color: #1c1726;
    font-size: 14px;
    font-weight: 800;
  }

  .music-label {
    margin-top: 18px;
  }

  .layout-card,
  .config-card {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid rgba(235, 232, 244, 0.92);
    background: rgba(255, 255, 255, 0.96);
    color: #1a1623;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(70, 56, 120, 0.035);
  }

  .layout-card {
    height: 138px;
    padding: 14px 18px 14px 14px;
    border-radius: 16px;
    gap: 15px;
  }

  .layout-card-image {
    width: 147px;
    height: 104px;
    display: block;
    flex: 0 0 auto;
    border-radius: 8px;
    object-fit: cover;
  }

  .layout-preview {
    width: 147px;
    height: 104px;
    display: grid;
    grid-template-columns: 1fr 72px;
    overflow: hidden;
    border: 1px solid #eff0f4;
    border-radius: 8px;
    background: #fbfdff;
  }

  .layout-preview.full-image {
    grid-template-columns: 1fr;
  }

  .layout-preview.full-image .text-preview {
    display: none;
  }

  .layout-preview.full-image .image-preview {
    position: relative;
  }

  .layout-preview.full-image .image-preview::after {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 14px;
    height: 20px;
    content: '';
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.72);
  }

  .layout-preview.left-right,
  .layout-preview.cross-page {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    padding: 9px;
    background: #fff;
  }

  .layout-preview.left-right .text-preview,
  .layout-preview.cross-page .text-preview {
    min-width: 0;
    padding: 0;
    border-radius: 6px;
    background:
      radial-gradient(circle at 72% 25%, #ffdf75 0 8px, transparent 9px),
      radial-gradient(circle at 45% 76%, #f7a4c5 0 14px, transparent 15px),
      linear-gradient(135deg, #ffe8ca 0%, #d8f4d0 45%, #a5d8ff 100%);
  }

  .layout-preview.left-right .text-preview i,
  .layout-preview.cross-page .text-preview i {
    display: none;
  }

  .layout-preview.left-right .image-preview,
  .layout-preview.cross-page .image-preview {
    border-radius: 6px;
  }

  .layout-preview.cross-page {
    gap: 0;
    padding: 8px;
    border-radius: 10px;
  }

  .layout-preview.cross-page .text-preview {
    border-radius: 7px 0 0 7px;
  }

  .layout-preview.cross-page .image-preview {
    border-left: 1px solid rgba(255, 255, 255, 0.82);
    border-radius: 0 7px 7px 0;
  }

  .text-preview {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    padding: 0 14px;
  }

  .text-preview i {
    width: 39px;
    height: 4px;
    border-radius: 999px;
    background: #dbe1ea;
  }

  .text-preview i:nth-child(2) {
    width: 53px;
  }

  .image-preview {
    background:
      radial-gradient(circle at 72% 25%, #ffe07a 0 8px, transparent 9px),
      radial-gradient(circle at 55% 72%, #7dd3fc 0 12px, transparent 13px),
      linear-gradient(135deg, #d8f4d0 0%, #bdebd5 45%, #f6d36d 100%);
  }

  .layout-card strong,
  .config-card strong {
    font-size: 15px;
    font-weight: 800;
  }

  .chevron {
    margin-left: auto;
    color: #111;
    font-size: 25px;
    line-height: 1;
  }

  .config-card {
    height: 51px;
    padding: 0 18px;
    border-radius: 12px;
    gap: 12px;
  }

  .voice-avatar {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #f5f5f5;
    overflow: hidden;
  }

  .voice-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .play-icon {
    width: 28px;
    color: #16121f;
    font-size: 14px;
  }

  .character-section {
    margin-top: 18px;
  }

  .character-section h3 {
    margin: 0 0 12px;
    color: #17131f;
    font-size: 18px;
    font-weight: 800;
  }

  .character-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
  }

  .character-card {
    width: 191px;
    flex: 0 0 auto;
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.86);
    box-sizing: border-box;
  }

  .text-character {
    padding: 36px 24px 16px;
  }

  .image-character {
    padding: 20px 20px 16px;
  }

  .multi-form-character {
    width: 236px;
    align-items: stretch;
    padding-inline: 16px;
  }

  .multi-form-character > img,
  .multi-form-character > .empty-avatar,
  .multi-form-character > strong,
  .multi-form-character > p,
  .multi-form-character > .form-count {
    align-self: center;
  }

  .form-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 22px;
    margin-top: 7px;
    padding: 0 9px;
    border-radius: 999px;
    background: #f3efff;
    color: #6730e7;
    font-size: 12px;
    font-weight: 800;
  }

  .form-list {
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin-top: 14px;
  }

  .form-item {
    display: grid;
    grid-template-columns: 46px 1fr;
    gap: 9px;
    align-items: center;
    padding: 8px;
    border-radius: 10px;
    background: #f7f3ff;
  }

  .form-item img,
  .form-empty {
    width: 46px;
    height: 54px;
    margin: 0;
    border-radius: 8px;
    background: #ede6ff;
    object-fit: contain;
  }

  .form-copy {
    min-width: 0;
  }

  .form-copy strong {
    display: block;
    overflow: hidden;
    color: #191421;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .form-copy span {
    display: -webkit-box;
    overflow: hidden;
    margin-top: 4px;
    color: #9b94aa;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .empty-avatar {
    width: 66px;
    height: 66px;
    position: relative;
    margin-bottom: 18px;
  }

  .empty-avatar::before {
    position: absolute;
    top: 0;
    left: 50%;
    width: 27px;
    height: 27px;
    content: '';
    border: 2px solid #111;
    border-radius: 50%;
    transform: translateX(-50%);
  }

  .empty-avatar::after {
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 48px;
    height: 31px;
    content: '';
    border: 2px solid #111;
    border-bottom: 0;
    border-radius: 48px 48px 0 0;
    transform: translateX(-50%);
  }

  .character-card img {
    width: 105px;
    height: 178px;
    object-fit: contain;
    margin-bottom: 15px;
  }

  .character-card strong {
    color: #191421;
    font-size: 15px;
    font-weight: 900;
  }

  .character-card p {
    margin: 12px 0 0;
    color: #a5a0af;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.24;
    text-align: center;
  }

  .text-character p {
    text-align: left;
  }

  .pricing-info {
    position: absolute;
    left: 50%;
    bottom: calc(100% + 8px);
    width: 260px;
    min-height: 191px;
    padding: 17px;
    border: 1px solid rgba(235, 232, 244, 0.92);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.96);
    box-sizing: border-box;
    box-shadow: 0 8px 24px rgba(70, 56, 120, 0.04);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateX(-50%) translateY(8px);
    transition:
      opacity 0.16s ease,
      transform 0.16s ease,
      visibility 0.16s ease;
  }

  /* 弹层底部透明延伸，接住从按钮移入的鼠标 */
  .pricing-info::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 12px;
  }

  .create-book-wrap:hover .pricing-info {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateX(-50%) translateY(0);
  }

  .pricing-info h4 {
    margin: 0 0 12px;
    color: #160211;
    font-size: 12px;
    font-weight: 800;
    line-height: 18px;
  }

  .price-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #6b7280;
    font-size: 12px;
    line-height: 18px;
  }

  .price-row + .price-row {
    margin-top: 8px;
  }

  .price-row strong {
    color: #160211;
    font-size: 12px;
    font-weight: 800;
  }

  .price-divider {
    height: 1px;
    margin: 9px 0 8px;
    background: #ebe8f4;
  }

  .total-row span {
    color: #160211;
    font-weight: 800;
  }

  .total-row strong {
    color: #6033e1;
  }

  .member-discount {
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 17px;
    padding: 0 12px;
    border: 0;
    border-radius: 10px;
    background: #fff4df;
    color: #d38743;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
  }

  .style-footer .next-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }

  .layout-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(17, 12, 32, 0.32);
  }

  .layout-modal {
    position: relative;
    width: min(690px, calc(100vw - 36px));
    min-height: 420px;
    padding: 32px 28px 24px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 24px 80px rgba(39, 31, 76, 0.22);
    box-sizing: border-box;
  }

  .layout-modal-close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9ca3af;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
  }

  .layout-modal-close:hover {
    background: #f3f4f6;
    color: #111827;
  }

  .layout-modal-title {
    margin: 0 0 18px;
    color: #160211;
    font-size: 16px;
    font-weight: 900;
    line-height: 22px;
  }

  .layout-modal-options {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    justify-content: center;
    gap: 20px 24px;
  }

  .layout-modal-option {
    width: 100%;
    min-height: 116px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 4px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: #fff;
    color: #160211;
    font: inherit;
    font-size: 11px;
    font-weight: 800;
    line-height: 16px;
    cursor: pointer;
    box-sizing: border-box;
  }

  .layout-modal-option.active,
  .layout-modal-option:hover,
  .layout-modal-option:focus-visible {
    border-color: #6730e7;
    outline: 0;
  }

  .layout-modal-option-preview {
    position: relative;
    width: 132px;
    height: 92px;
    flex-shrink: 0;
  }

  .layout-modal-option-image {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: 6px;
    object-fit: contain;
  }

  .layout-modal-option-preview .membership-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
    display: inline-block;
    width: 16px;
    height: 16px;
    overflow: hidden;
    border-radius: 4px;
    transition: width 0.22s ease;
  }

  .layout-modal-option-preview .membership-badge__compact,
  .layout-modal-option-preview .membership-badge__full {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    max-width: none;
    display: block;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  .layout-modal-option-preview .membership-badge__compact {
    width: 16px;
    opacity: 1;
  }

  .layout-modal-option-preview .membership-badge__full {
    width: 39px;
    opacity: 0;
  }

  .layout-modal-option:hover .layout-modal-option-preview .membership-badge--expandable {
    width: 39px;
  }

  .layout-modal-option:hover
    .layout-modal-option-preview
    .membership-badge--expandable
    .membership-badge__compact {
    opacity: 0;
  }

  .layout-modal-option:hover
    .layout-modal-option-preview
    .membership-badge--expandable
    .membership-badge__full {
    opacity: 1;
  }

  .layout-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 22px;
    padding-right: 2px;
  }

  .layout-modal-cancel,
  .layout-modal-confirm {
    width: 76px;
    height: 40px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
  }

  .layout-modal-cancel {
    border: 1px solid #ebe8f4;
    background: #fff;
    color: #4b5563;
  }

  .layout-modal-confirm {
    border: 0;
    background: #6730e7;
    color: #fff;
  }

  .layout-modal-cancel:hover,
  .layout-modal-confirm:hover,
  .layout-modal-cancel:focus-visible,
  .layout-modal-confirm:focus-visible {
    outline: 2px solid rgba(103, 48, 231, 0.24);
    outline-offset: 2px;
  }

  .art-style-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(17, 12, 32, 0.32);
  }

  .art-style-modal {
    position: relative;
    display: flex;
    flex-direction: column;
    width: min(760px, calc(100vw - 36px));
    height: 560px;
    max-height: calc(100vh - 48px);
    padding: 40px 24px 24px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 24px 80px rgba(39, 31, 76, 0.22);
    box-sizing: border-box;
    overflow: hidden;
  }

  .art-style-modal-close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9ca3af;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
  }

  .art-style-modal-close:hover {
    background: #f3f4f6;
    color: #111827;
  }

  .art-style-modal-title {
    flex-shrink: 0;
    margin: 0 0 16px 18px;
    color: #160211;
    font-size: 18px;
    font-weight: 900;
    line-height: 25px;
  }

  .art-style-model-tabs {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    margin: 0 0 18px;
    padding: 4px;
    border-radius: 10px;
    background: #f3f1f7;
  }

  .art-style-model-tab {
    min-height: 40px;
    padding: 8px 12px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #6e6a7c;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.35;
    text-align: center;
    cursor: pointer;
  }

  .art-style-model-tab.active {
    background: #fff;
    color: #160211;
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(72, 55, 133, 0.1);
  }

  .art-style-modal-options {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px 16px;
  }

  .art-style-modal-option {
    min-height: 142px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: #fff;
    color: #160211;
    font: inherit;
    font-size: 13px;
    font-weight: 800;
    line-height: 18px;
    cursor: pointer;
    box-sizing: border-box;
  }

  .art-style-modal-option.active,
  .art-style-modal-option:hover,
  .art-style-modal-option:focus-visible {
    border-color: #6730e7;
    outline: 0;
  }

  .art-style-preview {
    position: relative;
    width: 100%;
    aspect-ratio: 132 / 94;
    overflow: hidden;
    border-radius: 8px;
    background: #f4f1ff;
  }

  .art-style-preview::before {
    display: none;
  }

  .art-style-preview > img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .art-style-preview .membership-badge {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 1;
    display: inline-block;
    width: 16px;
    height: 16px;
    overflow: hidden;
    border-radius: 4px;
    transition: width 0.22s ease;
  }

  .art-style-preview .membership-badge__compact,
  .art-style-preview .membership-badge__full {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    max-width: none;
    display: block;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  .art-style-preview .membership-badge__compact {
    width: 16px;
    opacity: 1;
  }

  .art-style-preview .membership-badge__full {
    width: 39px;
    opacity: 0;
  }

  .art-style-modal-option:hover .art-style-preview .membership-badge--expandable {
    width: 39px;
  }

  .art-style-modal-option:hover
    .art-style-preview
    .membership-badge--expandable
    .membership-badge__compact {
    opacity: 0;
  }

  .art-style-modal-option:hover
    .art-style-preview
    .membership-badge--expandable
    .membership-badge__full {
    opacity: 1;
  }

  .art-style-modal-actions {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 18px;
    padding-right: 2px;
  }

  .art-style-modal-cancel,
  .art-style-modal-confirm {
    width: 76px;
    height: 40px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
  }

  .art-style-modal-cancel {
    border: 1px solid #ebe8f4;
    background: #fff;
    color: #4b5563;
  }

  .art-style-modal-confirm {
    border: 0;
    background: #6730e7;
    color: #fff;
  }

  .art-style-modal-cancel:hover,
  .art-style-modal-confirm:hover,
  .art-style-modal-cancel:focus-visible,
  .art-style-modal-confirm:focus-visible {
    outline: 2px solid rgba(103, 48, 231, 0.24);
    outline-offset: 2px;
  }

  .generation-overlay {
    position: fixed;
    inset: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
  }

  .service-error-modal {
    position: relative;
    width: 420px;
    max-width: calc(100vw - 48px);
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 24px 80px rgba(39, 31, 76, 0.22);
    overflow: hidden;
  }

  .service-error-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 36px 36px;
    text-align: center;
    box-sizing: border-box;
  }

  .service-error-icon {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    border-radius: 50%;
    background: #f0e9ff;
    color: #7c4dff;
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
  }

  .service-error-icon--support {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: #f1eeff;
  }

  .service-error-title {
    margin: 0 0 10px;
    color: #1f1635;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.4;
  }

  .service-error-message {
    margin: 0;
    max-width: 300px;
    color: #8b849c;
    font-size: 14px;
    line-height: 1.6;
  }

  .service-error-actions {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 28px;
  }

  .service-error-actions--support {
    gap: 12px;
    padding-top: 24px;
    border-top: 1px solid #f0edf5;
  }

  .service-error-action {
    min-width: 160px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .service-error-actions--support .service-error-action,
  .service-error-action-wrap {
    flex: 1;
    min-width: 0;
  }

  .service-error-action-wrap .service-error-action {
    width: 100%;
  }

  .service-error-action:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .service-error-action.secondary {
    border: 1px solid #ebe6f7;
    background: #fff;
    color: #6033e1;
  }

  .service-error-action.primary {
    border: 0;
    background: #6033e1;
    color: #fff;
  }

  .create-support-qr {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 4px 0 2px;
  }

  .create-support-qr img {
    display: block;
    width: 140px;
    height: 140px;
    object-fit: contain;
  }

  .create-support-qr p {
    margin: 0;
    color: #6d687a;
    font-size: 12px;
    line-height: 1.4;
    text-align: center;
  }

  :global(.create-support-qr-popper.el-popper) {
    z-index: 40 !important;
    inset: auto auto 200px 550px !important;
    transform: none !important;
    padding: 16px;
    border: 0;
    border-radius: 12px;
    box-shadow: 0 8px 28px rgba(39, 31, 76, 0.16);
    user-select: none;
    -webkit-user-select: none;
  }

  .generation-modal {
    position: relative;
    width: 509px;
    min-height: 390px;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 24px 80px rgba(39, 31, 76, 0.22);
    overflow: hidden;
  }

  /* complete-story.gif：左侧偏薰衣草白 → 右侧偏暖纸白 */
  .generation-modal--complete-gif {
    background: linear-gradient(90deg, #f2f0f9 0%, #f7f9f7 55%, #faf9f6 100%);
  }

  /* generate-images.gif：左侧偏冷亮白 → 右侧偏灰绿浅白 */
  .generation-modal--images-gif {
    background: linear-gradient(90deg, #f9fbff 0%, #f8fafb 48%, #f4f6f3 100%);
  }

  .modal-close {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9ca3af;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
  }

  .modal-close:hover {
    background: #f3f4f6;
    color: #111827;
  }

  .generating-content,
  .generation-done-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
  }

  .generating-content {
    padding: 35px;
  }

  .generation-title {
    margin-bottom: 12px;
    color: #6033e1;
    font-size: 20px;
    font-weight: 700;
    line-height: 22px;
  }

  .generation-subtitle {
    margin: 8px 0 0;
    color: #9ca3af;
    font-size: 13px;
    font-weight: 600;
    line-height: 17px;
  }

  .generation-gif {
    width: 260px;
    height: 146px;
    display: block;
    margin-block: 20px;
    object-fit: contain;
  }

  .progress-wrap {
    width: 285px;
    height: 31px;
    display: flex;
    align-items: center;
  }

  .progress-track {
    position: relative;
    width: 100%;
    height: 7px;
    overflow: hidden;
    border-radius: 999px;
    background: #efe5ff;
  }

  .progress-fill {
    height: 100%;
    border-radius: inherit;
    background: #6033e1;
    transition: width 0.25s ease;
  }

  .generation-tips {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin: 0;
    color: #9ca3af;
    font-size: 12px;
    line-height: 18px;
  }

  .generation-tip-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    object-fit: contain;
  }

  .generation-tips button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #6033e1;
    font: inherit;
    cursor: pointer;
  }

  .generation-done-content {
    min-height: 390px;
    padding: 82px 32px 28px;
  }

  .generation-done-partial {
    padding: 40px 32px 28px;
    align-items: stretch;
    text-align: left;
  }

  .done-partial-title {
    margin: 0;
    color: #1f1635;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
  }

  .done-partial-subtitle {
    margin: 8px 0 0;
    color: #8b849c;
    font-size: 14px;
    line-height: 1.5;
    text-align: center;
  }

  .done-status-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 28px;
  }

  .done-status-card {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 64px;
    padding: 14px 16px;
    border-radius: 12px;
    box-sizing: border-box;
  }

  .done-status-card.success {
    background: #f3eeff;
  }

  .done-status-card.warning {
    background: #fff6eb;
  }

  .done-status-icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
  }

  .done-status-icon.success {
    background: #7c4dff;
  }

  .done-status-icon.warning {
    background: #ff9f43;
  }

  .done-status-text {
    color: #5b3cc4;
    font-size: 15px;
    font-weight: 600;
  }

  .done-status-body {
    flex: 1;
    min-width: 0;
  }

  .done-status-body strong {
    display: block;
    color: #c56a16;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.4;
  }

  .done-status-body p {
    margin: 4px 0 0;
    color: #b08a5c;
    font-size: 12px;
    line-height: 1.4;
  }

  .done-tts-retry {
    flex-shrink: 0;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 12px;
    border: 1px solid #ff9f43;
    border-radius: 8px;
    background: #fff;
    color: #ff9f43;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .done-tts-retry:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .done-title-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .done-check {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #6033e1;
    color: #fff;
    font-size: 14px;
    font-weight: 900;
  }

  .done-title-row h2 {
    margin: 0;
    color: #6033e1;
    font-size: 16px;
    font-weight: 900;
    line-height: 22px;
  }

  .done-story-image {
    width: 260px;
    height: 146px;
    display: block;
    margin-block: 20px;
    object-fit: contain;
  }

  .modal-actions {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 34px;
    box-sizing: border-box;
  }

  .modal-action {
    height: 40px;
    border: 0;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
  }

  .modal-action.secondary {
    border: 1px solid #ebe8f4;
    background: #fff;
    color: #160211;
  }

  .modal-action.primary {
    background: #6033e1;
    color: #fff;
  }

  @keyframes bookFloat {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-6px);
    }
  }

  @media (max-width: 1180px) {
    .create-page {
      padding-inline: 28px;
    }

    .options-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .flow-footer {
      grid-template-columns: 100px 1fr 100px;
    }

    .style-panel {
      width: min(1000px, 100%);
      transform: none;
    }

    .style-settings {
      grid-template-columns: 1fr;
    }

    .pricing-info {
      left: 50%;
      bottom: calc(100% + 8px);
      margin-top: 0;
    }
  }

  /* iPad 横屏由工作区内容区滚动，避免 Safari 嵌套定高滚动截断底部操作按钮。 */
  @media (min-width: 768px) and (max-width: 1180px) and (hover: none) {
    .create-page {
      height: auto;
      min-height: 100%;
      overflow-y: visible;
      padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
    }

    .create-page.flow-page {
      align-items: flex-start;
      justify-content: center;
    }

    .script-panel--loading {
      min-height: 640px;
    }
  }

  @media (max-width: 767px) {
    /* 创作首页：页面定高，面板内滚动，底部 CTA 固定可见 */
    .create-page:not(.flow-page) {
      --create-mobile-cta-height: calc(74px + env(safe-area-inset-bottom, 0px));

      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      max-height: 100%;
      align-items: stretch;
      justify-content: flex-start;
      padding: 0;
      overflow: hidden;
      background: #fbf9ff;
    }

    .create-page.flow-page {
      height: auto;
      min-height: 100%;
      padding: 16px 16px calc(108px + env(safe-area-inset-bottom));
      overflow-x: hidden;
      overflow-y: visible;
    }

    .create-page::after {
      width: min(100%, 360px);
      height: 280px;
      top: 120px;
      transform: translate(-50%, 0);
      opacity: 0.8;
    }

    .create-page:not(.flow-page) .create-panel {
      flex: 1 1 auto;
      width: 100%;
      min-height: 0;
      align-items: stretch;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior-y: contain;
      padding: 20px 16px calc(var(--create-mobile-cta-height) + 105px);
      box-sizing: border-box;
    }

    .title-icon {
      margin-top: 8px;
      margin-bottom: 4px;
      text-align: center;
    }

    .title-icon-img {
      width: 28px;
      height: 28px;
      margin: 0 auto;
    }

    .create-title {
      width: 216px;
      height: 29px;
      margin: 0 0 24px;
      align-self: center;
      text-align: center;
    }

    .prompt-card {
      height: 280px;
      min-height: 280px;
      flex: 0 0 280px;
      display: grid;
      grid-template-rows: minmax(0, 1fr) 32px;
      padding: 16px;
      overflow: hidden;
      border-radius: 16px;
      box-shadow: none;
    }

    .prompt-card :deep(.prompt-mention-editor-wrap) {
      height: 100%;
      max-height: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .prompt-card :deep(.prompt-mention-editor) {
      /* iOS 聚焦时字号 < 16px 会自动放大页面，移动端必须保持 ≥16px */
      height: 100%;
      max-height: 100%;
      min-height: 0;
      font-size: 16px;
      line-height: 1.5;
    }

    .prompt-card :deep(.prompt-mention-editor:empty::before) {
      color: #6e6a7c;
    }

    .prompt-footer {
      min-height: 0;
      flex: none;
      align-items: center;
      gap: 8px;
    }

    .mention-wrap {
      z-index: auto;
    }

    .prompt-footer-leading {
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }

    .prompt-footer-leading::-webkit-scrollbar {
      display: none;
    }

    .mention-btn {
      width: auto;
      height: 32px;
      flex-shrink: 0;
      padding: 0 8px;
      border-radius: 999px;
      background: #f5f5f5;
      color: #707480;
      font-size: 0;
      font-weight: 400;
    }

    .mention-btn::before {
      content: '@ 选择角色';
      font-size: 13px;
    }

    .mention-tip {
      display: none;
    }

    .mention-tip::before {
      display: none;
    }

    .prompt-card :deep(.mention-chip) {
      gap: 4px;
      padding: 4px;
      border-radius: 4px;
      background: rgba(96, 51, 225, 0.05);
    }

    .prompt-card :deep(.mention-chip-label) {
      color: #160211;
      font-size: 13px;
      font-weight: 400;
    }

    .prompt-card :deep(.mention-chip-avatar) {
      width: 13px;
      height: 16px;
      border-radius: 0;
    }

    .count-text {
      flex-shrink: 0;
      color: #6e6a7c;
      font-size: 13px;
      font-weight: 400;
    }

    .options-grid {
      order: 2;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      margin-top: 16px;
    }

    .option-field--artStyle {
      order: 1;
      grid-column: 1 / -1;
    }

    .option-field--readerAge {
      order: 2;
    }

    .option-field--pageCount {
      order: 3;
    }

    .option-field--narrator {
      order: 4;
    }

    .option-field--language {
      order: 5;
    }

    .option-field {
      gap: 12px;
    }

    .option-label {
      color: #160211;
      font-size: 13px;
      font-weight: 400;
    }

    .option-select,
    .create-panel :deep(.create-option-trigger) {
      height: 42px;
      padding: 0 16px;
      border: 0;
      border-radius: 12px;
      background: #fff;
      color: #160211;
      font-size: 13px;
      font-weight: 400;
      box-shadow: none;
    }

    .create-panel :deep(.create-option-chevron),
    .option-chevron {
      margin-left: auto;
      color: #160211;
    }

    .option-art-style-trigger {
      height: auto;
      min-height: 96px;
      padding: 8px 16px 8px 8px;
      border-radius: 16px;
      gap: 12px;
    }

    .option-art-preview {
      display: block;
      width: 142px;
      height: 80px;
      flex: 0 0 auto;
      border-radius: 8px;
      object-fit: cover;
    }

    .generate-btn-wrap {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 12;
      width: 100%;
      height: var(--create-mobile-cta-height);
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
      box-sizing: border-box;
      background: #fbf9ff;
    }

    .generate-btn {
      height: 42px;
      margin-top: 0;
      border: 0;
      outline: 0;
      border-radius: 16px;
      background: #6033e1;
      font-size: 13px;
      font-weight: 400;
      box-shadow: none;
      -webkit-appearance: none;
      appearance: none;
    }

    .generate-btn:focus,
    .generate-btn:focus-visible {
      outline: 0;
      box-shadow: none;
    }

    .suggest-section {
      order: 1;
      margin-top: 16px;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    .suggest-title {
      gap: 0;
      margin-bottom: 12px;
      justify-content: center;
    }

    .suggest-title-line {
      display: none;
    }

    .suggest-title-text {
      width: 100%;
      color: #6e6a7c;
      font-size: 12px;
      font-weight: 400;
      text-align: center;
    }

    .suggest-tags {
      display: flex;
      flex-wrap: nowrap;
      justify-content: flex-start;
      gap: 8px;
      margin-top: 0;
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior-x: contain;
      scrollbar-width: none;
      margin-left: -16px;
      margin-right: -16px;
      padding: 0 16px 2px;
    }

    .suggest-tags::-webkit-scrollbar {
      display: none;
    }

    .suggest-tag {
      flex: 0 0 auto;
      height: 36px;
      padding: 0 16px;
      border-radius: 16px;
      background: #ede8ff;
      color: #6033e1;
      font-size: 13px;
      font-weight: 400;
      white-space: nowrap;
    }

    .suggest-tag:hover {
      background: #ede8ff;
      color: #6033e1;
    }

    .mention-popup-root--mobile {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }

    .mention-popup--mobile {
      position: relative;
      left: auto;
      top: auto;
      width: 100% !important;
      max-width: none;
      height: min(714px, calc(100dvh - 138px)) !important;
      max-height: calc(100dvh - 138px);
      padding: 16px;
      border: 0;
      border-radius: 16px 16px 0 0;
      box-shadow: none;
      --mention-scale: 1;
    }

    .mention-popup-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .mention-popup-title {
      color: #160211;
      font-size: 13px;
      font-weight: 400;
    }

    .mention-popup--mobile .mention-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .mention-popup--mobile .mention-char-card {
      padding: 20px 12px;
      border-radius: 12px;
      background: #fbf9ff;
    }

    .mention-popup--mobile .mention-char-img,
    .mention-popup--mobile .mention-char-placeholder {
      width: 142px;
      height: 142px;
    }

    .mention-popup--mobile .mention-create-btn {
      height: 42px;
      border-radius: 16px;
      font-size: 13px;
      font-weight: 400;
    }

    .flow-panel {
      width: 100%;
      padding-bottom: calc(74px + env(safe-area-inset-bottom));
    }

    .flow-head {
      align-items: center;
      margin-bottom: 16px;
    }

    .flow-title {
      font-size: 14px;
      font-weight: 400;
      color: #160211;
    }

    .flow-title-wrap span {
      font-size: 12px;
      font-weight: 400;
      color: #6e6a7c;
    }

    .refresh-btn {
      height: 32px;
      padding: 0 12px;
      border-radius: 999px;
      background: #fff;
      color: #160211;
      font-size: 12px;
      font-weight: 400;
      box-shadow: none;
    }

    .script-list--loading {
      min-height: 280px;
    }

    .script-loading {
      min-height: 220px;
      gap: 20px;
    }

    .outline-progress-ring {
      width: 96px;
      height: 96px;
    }

    .outline-progress-value {
      font-size: 22px;
    }

    .outline-progress-tip {
      padding: 0 24px;
      font-size: 13px;
      line-height: 20px;
    }

    .script-row,
    .storyboard-row {
      grid-template-columns: 30px 1fr;
      gap: 12px;
      align-items: start;
    }

    .script-row strong,
    .storyboard-row strong {
      padding-top: 23px;
      font-size: 13px;
      font-weight: 400;
      color: #160211;
    }

    .title-row strong {
      color: #160211;
    }

    .script-edit,
    .title-edit {
      min-height: 60px;
      height: auto;
      padding: 10px;
      border-radius: 16px;
      background: #f5f0ff;
      color: #160211;
      font-size: 13px;
      font-weight: 400;
      line-height: 1.5;
      overflow: hidden;
    }

    .title-edit {
      font-size: 16px;
    }

    .flow-footer,
    .style-footer {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 12;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 0;
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
      background: #fbf9ff;
      box-sizing: border-box;
      pointer-events: auto;
    }

    .flow-footer {
      grid-template-columns: none;
    }

    .back-btn {
      flex: 0 0 74px;
      width: 74px;
      height: 42px;
      padding: 0 16px;
      border-radius: 16px;
      background: #fff;
      color: #160211;
      font-size: 13px;
      font-weight: 400;
      box-shadow: none;
    }

    .back-btn span:first-child,
    .back-chevron {
      display: none;
    }

    .next-btn {
      flex: 1;
      width: auto;
      height: 42px;
      border-radius: 16px;
      background: #6033e1;
      font-size: 13px;
      font-weight: 400;
      box-shadow: none;
    }

    .style-panel {
      width: 100%;
      min-width: 0;
      padding-bottom: calc(74px + env(safe-area-inset-bottom));
    }

    .style-content {
      min-width: 0;
    }

    .style-title {
      display: none;
    }

    .style-settings {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .setting-label {
      margin-bottom: 12px;
      color: #160211;
      font-size: 13px;
      font-weight: 400;
    }

    .layout-card {
      height: 116px;
      padding: 8px 16px 8px 8px;
      border: 0;
      border-radius: 16px;
      box-shadow: none;
    }

    .layout-card-image {
      width: 140px;
      height: 100px;
      border-radius: 8px;
    }

    .config-card {
      height: 42px;
      padding: 0 16px;
      border: 0;
      border-radius: 12px;
      box-shadow: none;
    }

    .config-card strong {
      font-size: 13px;
      font-weight: 400;
    }

    .character-section h3 {
      font-size: 13px;
      font-weight: 400;
      color: #160211;
    }

    .style-footer {
      grid-template-columns: none;
      margin-top: 0;
    }

    .create-book-wrap {
      flex: 1;
      width: auto;
      min-width: 0;
    }

    .style-footer .next-btn {
      width: 100%;
      min-width: 0;
      padding: 0 16px;
    }

    .pricing-info {
      left: auto;
      right: 0;
      width: min(260px, calc(100vw - 112px));
      transform: translateY(8px);
    }

    .create-book-wrap:hover .pricing-info {
      transform: translateY(0);
    }

    .character-cards {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .character-card {
      width: 100%;
    }

    .storyboard-card {
      grid-template-columns: 1fr;
    }

    .layout-modal-overlay,
    .art-style-modal-overlay {
      align-items: flex-end;
    }

    .layout-modal,
    .art-style-modal {
      width: 100%;
      max-height: min(85dvh, 720px);
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
      border-radius: 16px 16px 0 0;
    }

    .layout-modal {
      min-height: auto;
      height: auto;
    }

    .art-style-modal {
      height: auto;
    }

    .layout-modal-title,
    .art-style-modal-title {
      margin: 0 0 16px;
      font-size: 13px;
      font-weight: 400;
      color: #160211;
    }

    .art-style-model-tabs {
      margin-bottom: 12px;
      padding: 3px;
      border-radius: 8px;
    }

    .art-style-model-tab {
      min-height: 36px;
      padding: 6px 8px;
      font-size: 11px;
      line-height: 1.3;
    }

    .layout-modal-options {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    .layout-modal-option {
      width: 100%;
    }

    .art-style-modal-options {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    .art-style-modal-actions {
      display: none;
    }

    .layout-modal-actions {
      display: none;
    }

    .art-style-modal-close,
    .layout-modal-close {
      top: 14px;
      right: 14px;
    }

    .art-style-preview {
      width: 100%;
    }

    .layout-preview {
      width: 120px;
    }

    .generation-overlay {
      align-items: flex-end;
    }

    .generation-modal {
      width: 100%;
      max-height: 90dvh;
      border-radius: 16px 16px 0 0;
    }

    .service-error-modal {
      width: 100%;
      max-width: 100%;
      max-height: 90dvh;
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -8px 40px rgba(39, 31, 76, 0.12);
    }

    .service-error-content {
      padding: 40px 24px calc(24px + env(safe-area-inset-bottom));
    }

    .service-error-actions--support {
      gap: 10px;
    }

    .service-error-action {
      min-width: 0;
      height: 44px;
      border-radius: 12px;
    }

    :global(.create-support-qr-popper.el-popper) {
      inset: auto !important;
      transform: none !important;
      left: 50% !important;
      right: auto !important;
      bottom: calc(88px + env(safe-area-inset-bottom)) !important;
      top: auto !important;
      margin-left: -90px;
    }
  }
</style>
